/**
 * Geometry transform helpers — pure math functions for translating,
 * mirroring, rotating, scaling and offsetting coordinate fields of any
 * DrawingEntity. Used by MOVE, COPY, MIRROR, ROTATE, SCALE, OFFSET,
 * STRETCH, TRIM, EXTEND, FILLET, CHAMFER, BREAK, JOIN, EXPLODE and
 * LENGTHEN modification commands.
 *
 * ## Function dependency map
 *
 * translatePoint(p, dx, dy) -> {x,y}
 * mirrorPoint(p, line1, line2) -> {x,y}
 * rotatePoint(p, center, angleDeg) -> {x,y}
 * scalePoint(p, base, factor) -> {x,y}
 * offsetLineSegment(start, end, distance, side) -> {start, end}
 * offsetCircle(center, radius, distance, side) -> {center, radius}
 * rotateEntityAngles(entity, angleDeg) -> entity
 * scaleEntityRadii(entity, base, factor) -> entity
 *   -> now lives in ./scaleEntityRadii.ts (2000-line ceiling split), re-exported
 *      here so this module's own `from "./geometryTransforms"` import path is unchanged.
 * transformEntityCoords(entity, fn) -> entity
 *   -> transformPoint / transformOptionalPoint / transformPoint3D / transformOptionalPoint3D
 *   -> switch on entity.type
 *   -> transformHatchBoundaryPaths (for Hatch entities)
 *
 * Batch 5 additions:
 * lineLineIntersection(seg1, seg2) -> Point2D | null
 * lineCircleIntersection(lineStart, lineEnd, center, radius) -> Point2D[]
 * lineArcIntersection(lineStart, lineEnd, center, radius, startAngle, endAngle) -> Point2D[]
 * trimLineAtPoint(start, end, trimPoint, keepSide) -> {start, end}
 * extendLineToPoint(start, end, targetPoint, nearEnd) -> {start, end}
 * filletTwoLines(l1s, l1e, l2s, l2e, radius, pickPoint1?, pickPoint2?) -> { line1, line2, arc | null }
 * chamferTwoLines(l1s, l1e, l2s, l2e, dist1, dist2) -> { line1, line2, chamferLine }
 * splitEntityAtPoints(entity, p1, p2) -> { before, removed, after }
 * explodePolyline(entity) -> DrawingEntity[]
 * joinContiguousEntities(entities, tolerance) -> DrawingEntity | null
 * lengthenLine(start, end, mode, value, nearEnd) -> {start, end}
 */

import type {
  DrawingEntity,
  HatchBoundaryPath,
  ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
// Value import (not `import type`): the heavy-POLYLINE bulge rule below is
// gated on the group-70 SUBTYPE, and `polylineSubtype` is the ONE decoder of
// those bits (`drawing-model/entityPolylineTableOle.ts` → `dxf-io`'s
// `dxfPolylineSubtype`, copied from ezdxf `Polyline.get_mode`). Re-deriving
// the bit test here would be a second truth for the file format.
import {
  isReflectedOcs as isReflectedOcsShared,
  ocsPlanarTransform,
  polylineSubtype,
  transformOcsNormal,
} from "@dxf-ai-editor/drawing-model";
import { probeAffine2d } from "./affine2dProbe";
import { ellipseEditFrame } from "./ellipseEditTransform";
// OLE2FRAME x an unrepresentable affine: the ONE "this edit declines this OLE
// frame" truth, in a leaf module so the shell reducer's future RU+EN audit row
// reads the same predicate this arm gates on (the shape `tableScaleRefusal`
// established for SCALE x ACAD_TABLE).
import { isAxisAlignedFramePreservingMap } from "./oleFrameTransformRefusal";
import { isOrientationReversingMap } from "./tableMirrorRefusal";
import { invalidateViewportClip } from "./viewportClipInvalidate";
import { withWorldAttributeRotation } from "./blockAttributeRotation";
// Codex MIRRTEXT-DIMENSION-MLEADER-ATTRIB — the pure axis-mirror angle math
// used below by `mirrorTextReadable`/`mirrorTextFull` now lives in its own
// ZERO-dependency module so `mirrorRotationField.ts` (DIMENSION/MULTILEADER/
// block-ATTRIB bare-rotation mirroring) can reuse the SAME rule without
// creating an import cycle back into this file. See `mirrorAngleMath.ts`'s
// header for the cycle-avoidance rationale.
import {
  reflectAngleAcrossAxis,
  readableAngleAcrossAxis,
} from "./mirrorAngleMath";

// ── Point-level transforms ──────────────────────────────────────────

type Point2D = { x: number; y: number };
type Point3D = { x: number; y: number; z: number };
type TransformFn = (p: Point2D) => Point2D;

/**
 * Translate a 2D point by (dx, dy).
 */
export function translatePoint(p: Point2D, dx: number, dy: number): Point2D {
  return { x: p.x + dx, y: p.y + dy };
}

/** Shared threshold for degenerate mirror-axis detection (used by mirrorPoint and computeInverse). */
export const MIRROR_AXIS_DEGENERATE_LENSQ = 1e-14;

/**
 * Mirror a 2D point across the line defined by `line1` → `line2`.
 *
 * Formula: reflect P across line AB.
 * Let d = B - A, t = dot(P - A, d) / dot(d, d),
 * foot = A + t * d, then P' = 2 * foot - P.
 */
export function mirrorPoint(
  p: Point2D,
  line1: Point2D,
  line2: Point2D,
): Point2D {
  const dx = line2.x - line1.x;
  const dy = line2.y - line1.y;
  const lenSq = dx * dx + dy * dy;

  // Degenerate mirror line (both points coincide) — return original
  if (lenSq < MIRROR_AXIS_DEGENERATE_LENSQ) {
    return { x: p.x, y: p.y };
  }

  const t = ((p.x - line1.x) * dx + (p.y - line1.y) * dy) / lenSq;
  const footX = line1.x + t * dx;
  const footY = line1.y + t * dy;

  return {
    x: 2 * footX - p.x,
    y: 2 * footY - p.y,
  };
}

/**
 * Reflect the XY of a 3D direction vector across an axis at angle `axisAngleDeg`
 * (degrees), PRESERVING the XY magnitude and the Z component. The reflection of
 * a 2D vector across an axis at angle α is the orthogonal map
 *   M = R(2α)·diag(1,−1):   vx' = cos(2α)·vx + sin(2α)·vy
 *                           vy' = sin(2α)·vx − cos(2α)·vy
 * which is magnitude-preserving (det = −1). This is exactly ezdxf's
 * `m.transform_direction(text_direction)` for a mirror matrix.
 *
 * Codex #342 F2: the prior call sites rebuilt the vector from `cos/sin` of the
 * REFLECTED SCALAR ANGLE — which silently NORMALISED it to unit length, losing a
 * non-unit XY magnitude (e.g. `{x:2,y:0,z:0.5}` → `{x:1,y:0,z:0.5}`). A planar
 * mirror must keep magnitude + Z. Shared one-truth so {@link mirrorTextReadable}
 * and {@link mirrorTextFull} don't clone the math (jscpd gate).
 *
 * GitHub Codex review PR #558 (formal bot review, HEAD 2d0dd928) — exported
 * so `apps/web`'s MIRROR reducer can reuse the SAME ezdxf-`transform_direction`
 * formula for MLeader CONTEXT_DATA MTextData's own `textDirection` (DXF group
 * 13/23/33) — confirmed via ezdxf's own MLeaderContext.MTextData docs to be
 * the SAME "horizontal text direction vector, higher priority than rotation
 * when both present" semantic as standalone MTEXT's `xAxisDirection`
 * (11/21/31), which this exact function already reflects for.
 */
export function reflectVectorXY(v: Point3D, axisAngleDeg: number): Point3D {
  const a = (2 * axisAngleDeg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: c * v.x + s * v.y, y: s * v.x - c * v.y, z: v.z };
}

/**
 * Rotate the XY of a 3D direction vector by 180° (negate X and Y), PRESERVING the
 * XY magnitude and the Z component. Used by {@link mirrorTextReadable} to bring a
 * geometrically-reflected, backward-pointing X-axis direction vector onto the
 * SAME forward-reading half-plane as the readable `rotation` scalar.
 *
 * Codex #342 round-6 (F-A / geometry+fidelity P2): in the MIRRTEXT=0 readable
 * MTEXT branch the `rotation` scalar carries a +180° readability adjustment (so
 * the run reads left-to-right) while the geometrically-reflected
 * {@link reflectVectorXY} vector does NOT — they diverge by 180° for a
 * backward-pointing reflected baseline. ezdxf/AutoCAD give the text-direction
 * VECTOR precedence over group 50 on re-read (`MText.get_rotation =
 * atan2(dir.y, dir.x)`), and this repo's writer emits group 11/21/31 for ANY
 * non-default vector, so a saved-then-reopened readable mirror would render
 * BACKWARD — defeating the purpose of MIRRTEXT=0. Negating the XY (180° rotation,
 * NOT a re-normalising cos/sin rebuild) aligns the vector with the readable
 * scalar while keeping the F2 magnitude/Z fix. Only applied when the readability
 * rule actually flipped — see {@link mirrorTextReadable}.
 *
 * GitHub Codex review PR #558 round 38 (formal bot review, HEAD ecca1c8d)
 * P2 — EXPORTED (was module-private) so `apps/web geometryReducerEdit.ts`
 * can reuse this SAME byte-identical readable-mode direction-vector
 * adjustment for MULTILEADER's `mTextData.textDirection` (DXF group
 * 13/23/33), which has the identical priority-over-rotation relationship
 * {@link reflectVectorXY}'s own doc already documents for the MIRRTEXT=1
 * case — see that reducer's own `reflectMLeaderTextDirectionReadable`
 * helper for the readable-mode call site. Zero logic change — export-only.
 */
export function negateVectorXY(v: Point3D): Point3D {
  return { x: -v.x, y: -v.y, z: v.z };
}

/**
 * Reflect the OCS extrusion NORMAL of a (possibly absent) mirrored-text entity
 * across the mirror axis at angle `axisAngleDeg`. An absent extrusion is the
 * (0,0,1) default ⇒ for any axis the XY stays zero and the Z flips, yielding the
 * canonical mirrored (0,0,−1).
 *
 * Codex #342 F3 (round-6 P2 → REOPENED): AutoCAD/ezdxf encode a mirror's det=−1
 * handedness in the OCS extrusion vector. ezdxf's
 * `transform_extrusion(extrusion, m)` builds the OCS x/y axes from the old
 * normal (Arbitrary Axis Algorithm), transforms BOTH through the matrix `M`,
 * and returns `normalize(M·ux × M·uy)`. For an orthogonal `M`,
 * `M·ux × M·uy = det(M)·M·(ux × uy) = det(M)·M·extrusion`. A MIRROR is a 3D
 * reflection with `det(M) = −1`, so the canonical result is
 *   new = −M·extrusion.
 * `M` here is the planar mirror across the XY line at the axis angle (Z fixed),
 * which {@link reflectVectorXY} computes (the `R(2α)·diag(1,−1)` block on XY,
 * Z passthrough). Therefore the correct op is:
 *   new = −reflectVectorXY(extrusion, axisAngle)   (negate ALL THREE components)
 *
 * The FIRST round-6 fix returned `{ r.x, r.y, −r.z }` (negate only Z). That is
 * `+M·v` in XY but `−M·v` in Z — the XY sign is the EXACT OPPOSITE of ezdxf's
 * for every non-axis-aligned normal (e.g. {1,0,0}@α=90° → it gave (−1,0,0) but
 * ezdxf gives (+1,0,0); {1,0,0}@α=45° → it gave (0,+1,0) but ezdxf gives
 * (0,−1,0)). Numerically verified against ezdxf `transform_extrusion` for axes
 * 0/30/45/90/135/17° and normals (1,0,0),(0.6,0,0.8),(0.5,0.5,0.707),
 * (−0.3,0.4,0.866): `−reflectVectorXY` matches to 1e-16 in EVERY case; the
 * Z-only-negation matched ONLY when XY was already zero (the default normal).
 *
 * Properties (all verified by tests):
 *   - default (absent / (0,0,1)) → reflectVectorXY gives (0,0,1) (XY zero) →
 *     negate all → (0,0,−1). The existing default-handedness case is preserved
 *     (the XY sign flip is a no-op when XY is zero).
 *   - involutive across the SAME axis: M is its own inverse, so −M(−M(v)) =
 *     M(M(v)) = v; a second mirror restores the original normal.
 *   - a non-default XY normal {x:1,y:0,z:0} across a vertical axis (α=90°) →
 *     reflectVectorXY → {−1,0,0} → negate all → {+1,0,0}, matching ezdxf
 *     (reflected, not stale, correct sign).
 *
 * Records the field that makes a TRUE mirror DISTINGUISHABLE from a plain 180°
 * rotation (MTEXT has no group-71) and round-trips via group 210/220/230. Shared
 * with {@link reflectVectorXY} (no math clone — only a sign flip on its output).
 * See {@link mirrorTextFull}.
 */
// Exported (GitHub Codex review PR #558 round 2, round-17 local read-only
// follow-up P2 — "Preserve the primary MLeader extrusion when mirroring" /
// "Reflect secondary MLeader extrusions without canonicalizing") — the
// MLeader mirror reducer (`geometryReducerEdit.ts`) needs this SAME
// general-purpose (arbitrary-tilt-preserving) reflection for its OWN
// CONTEXT_DATA `mTextData.extrusion` fields (primary `contextData` +
// `secondaryContexts[]`), which previously canonicalised a genuinely
// tilted (non-axis-aligned) source extrusion to the axis-aligned (0,0,±1)
// pair instead of properly reflecting it — losing the original OCS tilt on
// a mirrored-and-resaved MLeader.
export function reflectExtrusion(
  e: Point3D | undefined,
  axisAngleDeg: number,
): Point3D {
  const v = e ?? { x: 0, y: 0, z: 1 };
  const r = reflectVectorXY(v, axisAngleDeg);
  // new = −M·v (negate ALL three components): ezdxf transform_extrusion =
  // det(M)·M·extrusion and a MIRROR has det(M)=−1. The default (XY=0) still
  // yields (0,0,−1); a non-default XY normal reflects with the correct sign.
  return { x: -r.x, y: -r.y, z: -r.z };
}

/**
 * Is the MTEXT X-axis direction vector (group 11/21/31) a GENUINELY custom
 * direction, or the schema default `{1,0,0}` (equivalently undefined)?
 *
 * `mtextEntitySchema.xAxisDirection` DEFAULTS to `{x:1,y:0,z:0}` (not undefined),
 * so a rotation-driven MTEXT (angle in group 50, no custom group 11) arrives with
 * `{1,0,0}`. Per the DXF reference + ezdxf (`MText.get_rotation()` returns the
 * text_direction angle when set, else the rotation attribute), the X-axis
 * DIRECTION VECTOR takes PRECEDENCE over the rotation only WHEN it is custom.
 *
 * Shared one-truth predicate (Codex #342): both {@link mirrorTextReadable} and
 * the `transformEntityCoords` MText case key off this so neither manufactures a
 * spurious non-default group 11 from a default `{1,0,0}` rotation-driven MTEXT.
 */
export function isCustomXAxisDirection(
  dir: Point3D | undefined,
): dir is Point3D {
  return (
    dir !== undefined && !(dir.x === 1 && dir.y === 0 && (dir.z ?? 0) === 0)
  );
}

/**
 * The EFFECTIVE on-screen MTEXT angle (degrees CCW) — the value a RENDERER /
 * preview must place the glyph run at. Per the DXF reference + ezdxf
 * `MText.get_rotation()`, the X-axis DIRECTION VECTOR (group 11/21/31) takes
 * PRECEDENCE over the `rotation` attribute (group 50) **when it is custom**:
 *   get_rotation = atan2(dir.y, dir.x)   if a custom text_direction is set
 *                = rotation              otherwise
 *
 * Codex #342 round-8 Finding A: the live render/preview read `entity.rotation`
 * directly (`mtextFromEntity` / `buildMTextPreview` → `geometry.rotation` →
 * `mesh.rotation.z`), so a group-11-only MTEXT (rotation 0, direction e.g. 30°)
 * rendered at 0° — the WRONG angle. round-7 deliberately stores the REFLECTED
 * RAW `rotation` field for export self-inverse, so the render MUST NOT consume
 * that raw scalar when a custom direction is present; it must derive the
 * effective angle from the vector. This is the ONE shared truth both render
 * readers (and the producer) call so they cannot drift. Non-custom (default
 * `{1,0,0}` / absent) → returns `rotation` verbatim ⇒ golden-parity byte-identical.
 */
export function effectiveMTextAngleDeg(
  dir: Point3D | undefined,
  rotation: number,
): number {
  return isCustomXAxisDirection(dir)
    ? (Math.atan2(dir.y, dir.x) * 180) / Math.PI
    : rotation;
}

/**
 * Does the MTEXT OCS extrusion NORMAL describe a REFLECTED (left-handed) glyph
 * frame when rendered TOP-DOWN (the editor's world-XY projection)?
 *
 * Codex #342 round-8 Finding B: the render derived the mirror flag from
 * `extrusion.z < 0` written as a brittle raw comparison, which mishandles `−0`
 * and the floating noise a planar reflection leaves on Z. This is the ONE
 * shared full-vector predicate both render readers (legacy bag producer +
 * typed `mtextFromEntity`) call, so the handedness test cannot drift.
 *
 * ## Why the test is `n.z < 0` (NOT a heuristic over the full XY)
 *
 * The DXF Arbitrary Axis Algorithm builds a RIGHT-HANDED OCS basis
 * `(ocsX, ocsY, n)` with `ocsX × ocsY = n` by construction. The top-down render
 * projects the glyph plane onto world XY and drops Z; the 2D handedness of the
 * projected glyph frame is
 *   det([ocsX_xy | ocsY_xy]) = ẑ · (ocsX × ocsY) = **n.z** (exactly).
 * So:
 *   - `n.z > 0`  → right-handed in TOP  → readable (NOT mirrored).
 *   - `n.z < 0`  → left-handed in TOP   → MIRRORED (glyph frame flips).
 *   - `n.z ≈ 0`  → the glyph plane is EDGE-ON to the top view: the projection is
 *     SINGULAR and handedness is geometrically UNDEFINED. AutoCAD/ezdxf model
 *     the OCS as strictly right-handed (ezdxf OCS docs); there is no in-plane
 *     handedness bit to recover. round-7's `reflectExtrusion` mirror of an
 *     in-plane SOURCE round-trips to itself (`(1,0,0)`@90° → `(1,0,0)`), so the
 *     normal alone cannot distinguish a mirrored from an unmirrored in-plane
 *     MTEXT — they are the SAME shape. Treating it as mirrored would be a 2D
 *     heuristic that contradicts the geometry (verified analytically + ezdxf).
 *
 * Critically, mirroring the COMMON case — a DEFAULT `(0,0,1)` MTEXT across ANY
 * axis — yields exactly `(0,0,−1)` (XY stays zero), so the real-world MIRRTEXT=1
 * regression IS fixed: every freshly-mirrored default MTEXT reports `mirrored`.
 * An in-plane (z≈0) source is only ever an IMPORTED edge-on plane, undefined
 * top-down either way.
 *
 * Default (absent / `(0,0,1)` / any pure +Z) → `false` ⇒ golden-parity
 * byte-identical. Numerically consistent with round-7's `reflectExtrusion`
 * case matrix (`(0,0,1)`→`(0,0,−1)`=mirrored; tilted `(0.6,0,0.8)`→
 * `(−0.6,0,−0.8)`=mirrored; the involutive `(1,0,0)`↔`(1,0,0)` in-plane pair =
 * undefined/false on both sides).
 */
export function isReflectedOcs(normal: Point3D | undefined): boolean {
  // DELEGATED, not re-derived: `drawing-model/wcsBoundsMath.ts::isReflectedOcs` owns the
  // comparison (and the near-zero tolerance the `z ≈ 0` edge-on case needs) because the MTEXT
  // BOUNDS and SELECT boxes need the identical bit and may not import this package — the same
  // "lowest shared package" relocation `drawing-model/vec3.ts` records (CLAUDE.md dup:check
  // one-truth). The wrapper stays so every `from "@dxf-ai-editor/command-engine"` import site
  // and this module's own derivation above keep working unchanged.
  return isReflectedOcsShared(normal);
}

/**
 * Mirror a text-bearing entity (TEXT / MTEXT) with **MIRRTEXT=0**
 * (AutoCAD's default) semantics: the insertion / alignment **point
 * position** reflects across the axis, but the glyph run stays
 * **readable** — the text is never flipped backwards or upside-down.
 *
 * AutoCAD reference: MIRROR + MIRRTEXT system variable. With MIRRTEXT=0
 * AutoCAD reflects the anchor point(s) and re-derives the text rotation so
 * the baseline points "forward" (left-to-right). Using the same convention
 * as {@link mirrorTextFull} (α = axis angle, θ = text/baseline rotation), the
 * pure geometric reflection of a baseline at angle `θ` across an axis at
 * angle `α` is `2α − θ`; that alone would read backwards, so AutoCAD adds
 * 180° when the reflected baseline points leftward (`cos(2α − θ) < 0`) to keep
 * the run readable while preserving the on-screen position. ezdxf mirrors this
 * by NOT setting the TEXT generation-flag backward (2) / upside-down (4) bits.
 *
 * For the common axis-aligned cases this preserves the original rotation:
 *   - horizontal axis (α=0) of horizontal text (θ=0): 2·0−0 = 0 → readable
 *   - vertical axis (α=90°) of horizontal text (θ=0): 2·90−0 = 180 →
 *     cos(180°) < 0 → +180 → 0 → upright, just repositioned.
 *
 * **NOT self-inverse for the rotation.** The position reflection is a perfect
 * involution, but the readable-rotation rule is many-to-one (the +180 flip
 * collapses backward baselines onto their forward twin), so applying it twice
 * does NOT recover the original rotation for text whose baseline pointed
 * backward (θ ∈ (90°,270°)): e.g. θ=210° across a vertical axis → 330° → 30°,
 * off by 180°. This is inherent — a single scalar can't carry the reflection's
 * sign once readability collapses it. Undo therefore does NOT re-mirror the
 * rotation: computeInverse (deleteSource=true) snapshots the original rotation
 * into the inverse command's `restoreRotations` map and the reducer rewrites it
 * exactly after the (involutive) position re-mirror. AutoCAD parity holds — the
 * forward result is always readable and on the correct mirrored side; only the
 * undo path uses the snapshot, not a blind self-inverse.
 *
 * Reused by the reducer for the MIRRTEXT=0 gated case; MIRRTEXT=1 keeps the
 * full geometric reflection via {@link transformEntityCoords}. Non-text
 * entities never reach here. Reuses {@link mirrorPoint} (no math clone).
 */
export function mirrorTextReadable<T extends DrawingEntity>(
  entity: T,
  line1: Point2D,
  line2: Point2D,
): T {
  // Degenerate axis — mirrorPoint already no-ops; keep the entity verbatim.
  const dx = line2.x - line1.x;
  const dy = line2.y - line1.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < MIRROR_AXIS_DEGENERATE_LENSQ) {
    return entity;
  }

  // Axis angle θ (degrees). Readable rotation = reflect the baseline, then
  // flip forward (+180) when it would read backwards.
  const axisAngleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  // Returns BOTH the readable angle AND whether the +180° readability adjustment
  // was applied. The `flipped` flag lets the MTEXT branch bring the
  // geometrically-reflected direction VECTOR onto the same forward half-plane as
  // the scalar (Codex #342 round-6 F-A) so vector⇄rotation agree on re-read.
  // Codex MIRRTEXT-DIMENSION-MLEADER-ATTRIB: the angle math itself (reflect,
  // then +180 when it would read backwards) is now the shared
  // `readableAngleAcrossAxis` (`mirrorAngleMath.ts`) — DIMENSION/MULTILEADER/
  // block-ATTRIB bare rotation fields reuse the SAME rule via
  // `mirrorRotationField.ts` so this local closure just binds `axisAngleDeg`.
  const readableRotation = (
    rotation: number,
  ): { angle: number; flipped: boolean } =>
    readableAngleAcrossAxis(rotation, axisAngleDeg);

  if (entity.type === "Text") {
    return {
      ...entity,
      insertionPoint: transformPoint(entity.insertionPoint, (p) =>
        mirrorPoint(p, line1, line2),
      ),
      alignmentPoint: transformOptionalPoint(entity.alignmentPoint, (p) =>
        mirrorPoint(p, line1, line2),
      ),
      rotation: readableRotation(entity.rotation).angle,
    };
  }

  if (entity.type === "MText") {
    // MTEXT carries BOTH group 50 (rotation) and group 11/21/31 (X-axis
    // direction vector). Per the DXF reference + ezdxf (`MText.get_rotation()`
    // returns the text_direction angle when set, else the rotation attribute),
    // the X-axis DIRECTION VECTOR takes PRECEDENCE when present — AutoCAD
    // derives the on-screen angle from `atan2(dir.y, dir.x)`. So for an MTEXT
    // imported with its angle in group 11 (rotation left at default 0), the
    // EFFECTIVE source angle is the direction-vector angle, NOT the rotation.
    // Codex #342 F3: read the effective angle from a non-default xAxisDirection,
    // apply the readable-mirror rule for the scalar `rotation`, AND reflect the
    // direction vector GEOMETRICALLY (Codex #342 F2: magnitude- + Z-preserving,
    // not a cos/sin rebuild that would normalise the XY length) so the writer
    // re-emits the REFLECTED direction, not the stale (un-reflected) one. For a
    // backward-pointing baseline the geometric vector and the readable scalar
    // may differ by 180°; the vector wins on re-read (ezdxf precedence).
    // `entity` is narrowed to the MText member here; type `next` to that member
    // (not the wide `T`) so the `xAxisDirection` write below is well-typed. The
    // function still returns `T` (the literal is assignable back at `return`).
    type MTextEntity = Extract<DrawingEntity, { type: "MText" }>;
    const mtext = entity as MTextEntity;
    const dir = mtext.xAxisDirection;
    const hasCustomDir = isCustomXAxisDirection(dir);
    const sourceAngleDeg = hasCustomDir
      ? (Math.atan2(dir.y, dir.x) * 180) / Math.PI
      : mtext.rotation;
    const { angle: readableAngleDeg, flipped: readableFlipped } =
      readableRotation(sourceAngleDeg);
    const next: MTextEntity = {
      ...mtext,
      insertionPoint: transformPoint(mtext.insertionPoint, (p) =>
        mirrorPoint(p, line1, line2),
      ),
      rotation: readableAngleDeg,
    };
    if (hasCustomDir) {
      // Codex #342 F2: reflect the ORIGINAL direction vector geometrically
      // (magnitude- AND Z-preserving) rather than rebuilding it from cos/sin of
      // the scalar readable angle (which normalised away a non-unit XY length).
      //
      // Codex #342 round-6 F-A (geometry + fidelity P2): the readable `rotation`
      // scalar above carries a +180° readability adjustment when the reflected
      // baseline would point backward, but the geometric vector does NOT — so for
      // a backward-pointing reflected baseline the two would disagree by 180°.
      // ezdxf/AutoCAD give the text-direction VECTOR precedence over group 50 on
      // re-read (`MText.get_rotation = atan2(dir.y, dir.x)`), and this repo's
      // writer emits group 11/21/31 for ANY non-default vector, so the divergent
      // vector would make a saved-then-reopened readable mirror render BACKWARD —
      // defeating MIRRTEXT=0. Bring the vector onto the SAME forward half-plane as
      // the readable scalar by a magnitude-preserving 180° XY rotation
      // ({@link negateVectorXY}, NOT a re-normalising rebuild) whenever the
      // readability rule flipped — keeping vector⇄rotation agreement AND the F2
      // magnitude/Z fix. When no flip happened the geometric reflection already
      // agrees with the scalar, so the vector is emitted as-is.
      const reflected = reflectVectorXY(dir, axisAngleDeg);
      next.xAxisDirection = readableFlipped
        ? negateVectorXY(reflected)
        : reflected;
    }
    return next as unknown as T;
  }

  // Not a text-bearing entity — caller should not have routed it here, but
  // be defensive: fall back to a plain coordinate mirror.
  return transformEntityCoords(entity, (p) => mirrorPoint(p, line1, line2));
}

/**
 * DXF TEXT group-71 generation-flag bits (Autodesk DXF reference).
 *
 * - bit 2 = BACKWARD: glyphs reflected across the local Y axis = `diag(−1,1)`,
 *   i.e. the READING direction is reversed (reflect across a VERTICAL line in
 *   the glyph frame).
 * - bit 4 = UPSIDE_DOWN: glyphs reflected across the local X axis = `diag(1,−1)`,
 *   i.e. the up direction is flipped (reflect across a HORIZONTAL line).
 *
 * The MIRRTEXT=1 mirror uses the UPSIDE_DOWN bit uniformly — see
 * {@link mirrorTextFull} for the derivation. BACKWARD is kept exported as the
 * canonical sibling bit (the writer/bridge round-trip both 71 bits) and for the
 * vertical-axis equivalence documented in {@link mirrorTextFull}.
 */
export const TEXT_GEN_FLAG_BACKWARD = 2; // mirrored across local Y (reversed read)
export const TEXT_GEN_FLAG_UPSIDE_DOWN = 4; // mirrored across local X (flipped up)

/**
 * Mirror a text-bearing entity (TEXT / MTEXT) with **MIRRTEXT=1** semantics:
 * the FULL geometric reflection. Unlike {@link mirrorTextReadable} (MIRRTEXT=0,
 * which re-derives a forward-reading rotation and never flips the glyphs), this
 * produces a TRULY mirrored — backward / upside-down — text for EVERY mirror
 * axis (axis-aligned AND diagonal) and BOTH entity types, with no per-axis gap.
 *
 * ## TEXT — the UNIFORM (geometrically-exact) rule
 *
 * A reflection across an axis at angle α decomposes as
 *   `reflect_α = R(2α) ∘ reflect_Xaxis`     (det = −1).
 * Applied to a readable TEXT whose glyph frame is `R(θ)`:
 *   `M·R(θ) = R(2α)·diag(1,−1)·R(θ) = R(2α)·R(−θ)·diag(1,−1) = R(2α−θ)·diag(1,−1)`.
 * `diag(1,−1)` is exactly the UPSIDE_DOWN (local-Y negated, group-71 bit 4)
 * pre-multiplier. So the mirror of a TEXT is, for EVERY axis (vertical,
 * horizontal, ±45°, arbitrary), **uniformly**:
 *   `rotation' = normalizeDeg360(2α − θ)`   AND   `generationFlags ^= bit-4`.
 * There is NO axis-conditional branch and NO "diagonal → no flag" gap (the
 * round-2..4 churn came from an axis-conditional bit-2/bit-4/none scheme that
 * was geometrically WRONG: vertical produced upside-down-not-backward, and the
 * diagonal produced an UNMIRRORED pure rotation, det = +1).
 *
 * Why bit-4, not bit-2: the residual after extracting `R(2α−θ)` is always
 * `diag(1,−1)` (negate local-Y = UPSIDE_DOWN). bit-2 BACKWARD = `diag(−1,1)` is
 * a DIFFERENT det=−1 primitive (reflect across local-Y, reverse reading) and
 * does not factor out of `M = R(2α)·diag(1,−1)`.
 *
 * Vertical-axis equivalence (documented, not a special case): for α=90°, θ=0 the
 * uniform rule yields rotation 180 + bit-4 = `R(180)·diag(1,−1) = diag(−1,1)`,
 * which is geometrically IDENTICAL to AutoCAD's canonical "rotation 0 + BACKWARD
 * (bit-2)" encoding `R(0)·diag(−1,1) = diag(−1,1)`. Both render the same backward
 * glyph run; the uniform bit-4 form is the involution-clean choice.
 *
 * ezdxf cross-reference (alternative encoding, NOT chosen here): ezdxf's
 * `Text.transform` reflects rotation via `ocs.transform_deg_angle` (= `2α − θ`)
 * and records the FLIP in the OCS extrusion vector `(0,0,−1)`, never touching
 * the group-71 bits ("a mirrored text cannot be represented by extrusion
 * (0,0,1)" — ezdxf upright docs). This repo's model carries BOTH `generationFlags`
 * (group 71) and `extrusion`; we use the group-71 form because it matches the
 * AutoCAD Properties "Backward/Upside down" surface and the writer emits group
 * 71 directly — avoiding OCS↔WCS round-trip churn. Emitting `extrusion=(0,0,−1)`
 * would be the ezdxf-faithful alternative; it is intentionally not used here.
 *
 * - **MTEXT** (`ezdxf/entities/mtext.py` `MText.transform`): the SINGLE source
 *   of truth is the text-direction vector. ezdxf first converts any rotation
 *   (group 50) into the direction vector, then transforms the VECTOR through the
 *   matrix (a mirror reflects it). `get_rotation = atan2(dir.y, dir.x)`. We
 *   mirror this: derive the source angle (custom direction vector wins over
 *   `rotation`, per {@link isCustomXAxisDirection}), reflect it (2α − θ, α = axis
 *   angle, θ = source angle — same `reflectAngle` used for TEXT), and write a
 *   consistent `rotation`; when the source had a custom direction we ALSO write
 *   the GEOMETRICALLY-reflected `xAxisDirection` ({@link reflectVectorXY}, Codex
 *   #342 F2 — magnitude- + Z-preserving, NOT a cos/sin rebuild that normalised
 *   the XY length) so the writer (which emits group 11/21/31 only for a
 *   non-default vector) re-emits the reflected direction. For a default-direction
 *   MTEXT we leave the vector default and let the reflected `rotation` govern
 *   (matches the group-11-only-when-non-default writer rule).
 *
 *   Codex #342 F3 — MTEXT HANDEDNESS: MTEXT has NO group-71 generation flags, so
 *   a default-horizontal MTEXT mirrored across a vertical axis (rotation 180)
 *   would be INDISTINGUISHABLE from a plain 180° rotation. AutoCAD/ezdxf encode
 *   the det=−1 mirror handedness in the OCS EXTRUSION vector (group 210/220/230 =
 *   (0,0,−1)): `MText.transform` runs `transform_extrusion(extrusion, m)` →
 *   (0,0,−1) for a mirror. We record this via {@link reflectExtrusion}, which
 *   reflects the FULL OCS normal across the axis (Codex #342 round-6 F3 — XY too,
 *   not only Z) and is involutive (a second mirror restores the original normal /
 *   the (0,0,1) default). This is the field that makes
 *   reload/export distinguish a TRUE mirror from a rotation and round-trips the
 *   handedness through the dxf-io COMMON 210 channel + the bridge. The
 *   MIRRTEXT=0 readable path does NOT flip extrusion (readable text is not
 *   handedness-mirrored). Render of the mirrored frame is wired into the live
 *   mesh path via the `mirrored` SceneGeometry flag (extrusion z < 0).
 *
 * ## Involution / undo
 *
 * A pure 2D mirror across a fixed axis is its own inverse: reflecting `2α − θ`
 * again gives `2α − (2α − θ) = θ`; the group-71 XOR is involutive; point/vector
 * reflection is involutive. The EFFECTIVE (on-screen) orientation is self-inverse
 * for every text type/axis. The one storage caveat (Codex #342 round-3 FINDING
 * A): a CUSTOM-direction MTEXT stores the angle in BOTH `rotation` and
 * `xAxisDirection`; the inverse re-reads the angle from the still-custom vector
 * (ezdxf get_rotation precedence), so the VECTOR returns exactly but the raw
 * `rotation` does NOT. The writer emits group 50 for any truthy `rotation`, so
 * to keep undo BIT-exact (no spurious group 50) the mirror command captures the
 * pre-mutation rotation+vector into restoreRotations/restoreXAxisDirs for
 * MIRRTEXT=1 too — see `computeInverse` in geometryMutations.ts. TEXT and
 * default-direction MTEXT are genuinely self-inverse; the capture is a harmless
 * no-op for them. Codex #342 F3: the extrusion Z-flip is itself involutive, but
 * the mirror command ALSO snapshots the pre-mutation extrusion into
 * `restoreExtrusions` so undo restores an MTEXT that had NO group 210 to exactly
 * ABSENT (not an emitted (0,0,1)) — keeping the round-trip byte-exact.
 *
 * Reuses {@link mirrorPoint}, `normalizeDeg360` (`mirrorAngleMath.ts`),
 * {@link reflectVectorXY}, {@link reflectExtrusion},
 * {@link isCustomXAxisDirection} and {@link transformOptionalPoint} — no math
 * clone. The TEXT group-71 toggle is the uniform UPSIDE_DOWN
 * ({@link TEXT_GEN_FLAG_UPSIDE_DOWN}) XOR inlined below. Non-text entities fall
 * back to a plain coordinate mirror (caller should not route them here).
 */
export function mirrorTextFull<T extends DrawingEntity>(
  entity: T,
  line1: Point2D,
  line2: Point2D,
): T {
  const dx = line2.x - line1.x;
  const dy = line2.y - line1.y;
  const lenSq = dx * dx + dy * dy;
  // Degenerate axis — no reflection happened; keep the entity verbatim.
  if (lenSq < MIRROR_AXIS_DEGENERATE_LENSQ) {
    return entity;
  }

  // Axis angle α (degrees). Geometric reflection of a baseline angle θ = 2α − θ.
  // Codex MIRRTEXT-DIMENSION-MLEADER-ATTRIB: shared `reflectAngleAcrossAxis`
  // (`mirrorAngleMath.ts`) — same rule DIMENSION/MULTILEADER/block-ATTRIB bare
  // rotation fields reuse via `mirrorRotationField.ts`.
  const axisAngleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const reflectAngle = (angleDeg: number): number =>
    reflectAngleAcrossAxis(angleDeg, axisAngleDeg);

  if (entity.type === "Text") {
    return {
      ...entity,
      insertionPoint: transformPoint(entity.insertionPoint, (p) =>
        mirrorPoint(p, line1, line2),
      ),
      alignmentPoint: transformOptionalPoint(entity.alignmentPoint, (p) =>
        mirrorPoint(p, line1, line2),
      ),
      // UNIFORM rule (every axis): rotation' = 2α − θ AND toggle the group-71
      // UPSIDE_DOWN bit. reflect_α = R(2α)·diag(1,−1) ⇒ M·R(θ) = R(2α−θ)·diag(1,−1),
      // where diag(1,−1) is exactly UPSIDE_DOWN (bit 4). XOR → involutive, so a
      // double-mirror restores the original (self-inverse undo). See doc above.
      rotation: reflectAngle(entity.rotation),
      generationFlags:
        (entity.generationFlags ?? 0) ^ TEXT_GEN_FLAG_UPSIDE_DOWN,
    };
  }

  if (entity.type === "MText") {
    type MTextEntity = Extract<DrawingEntity, { type: "MText" }>;
    const mtext = entity as MTextEntity;
    const dir = mtext.xAxisDirection;
    const hasCustomDir = isCustomXAxisDirection(dir);
    // The EFFECTIVE source angle: a custom direction vector WINS over the rotation
    // attribute (ezdxf get_rotation precedence); otherwise the angle lives in
    // `rotation`. Used both to reflect the vector and — only on the collapse case
    // below — to carry the angle when the vector degrades to the default axis.
    const sourceAngleDeg = hasCustomDir
      ? (Math.atan2(dir.y, dir.x) * 180) / Math.PI
      : mtext.rotation;
    // Codex #342 F2: reflect the direction vector GEOMETRICALLY (magnitude- +
    // Z-preserving) rather than rebuilding it from cos/sin of a scalar angle
    // (which normalised away a non-unit XY length). Computed BEFORE choosing the
    // stored rotation so the rotation rule can react to a vector that reflects
    // exactly onto the {1,0,0} default axis (the collapse case below).
    const reflectedDir = hasCustomDir
      ? reflectVectorXY(dir, axisAngleDeg)
      : dir;
    const reflectedStaysCustom = isCustomXAxisDirection(reflectedDir);
    const next: MTextEntity = {
      ...mtext,
      insertionPoint: transformPoint(mtext.insertionPoint, (p) =>
        mirrorPoint(p, line1, line2),
      ),
      // Codex #342 round-6 P2 (F4): the stored `rotation` rule.
      //
      // When the reflected direction vector STAYS custom it is the source of
      // truth (the writer emits group 11/21/31 and ezdxf get_rotation reads the
      // vector), so `rotation` is REDUNDANT — reflect the RAW `rotation` FIELD so
      // it is SELF-INVERSE (`reflectAngle∘reflectAngle = id`). A user MIRRORing a
      // group-11-only MTEXT (rotation:0, custom dir) TWICE across the same axis
      // then exports the ORIGINAL `rotation:0` — NOT the spurious group 50 the
      // prior vector-derived value produced (rotation:0 → 30 after a double
      // mirror across the same axis).
      //
      // When the reflected vector COLLAPSES exactly onto the {1,0,0} default axis
      // (e.g. a 180°-pointing dir mirrored across a perpendicular axis), the
      // vector can no longer carry the angle — so `rotation` MUST carry the
      // EFFECTIVE reflected angle (`reflectAngle(sourceAngleDeg)`) or the on-screen
      // orientation would be lost. This still double-mirrors back to the original
      // effective angle (the next mirror is rotation-driven and reflectAngle is
      // involutive). For a DEFAULT-direction source `sourceAngleDeg === rotation`,
      // so both branches collapse to `reflectAngle(rotation)` — byte-identical to
      // the default / TEXT-equivalent path (no behaviour change there).
      rotation: reflectedStaysCustom
        ? reflectAngle(mtext.rotation)
        : reflectAngle(sourceAngleDeg),
      // Codex #342 F3 (round-6 P2): record the det=−1 handedness by reflecting the
      // FULL OCS extrusion NORMAL across the mirror axis (not only flipping Z), so
      // a source MTEXT that already carries a NON-default extrusion with XY
      // components reflects its XY too (ezdxf `transform_extrusion`). A TRUE mirror
      // is then distinguishable from a 180° rotation (MTEXT has no group-71) and
      // round-trips via group 210/220/230. Involutive across the same axis; the
      // default (absent/(0,0,1)) → (0,0,−1) handedness case is preserved.
      extrusion: reflectExtrusion(mtext.extrusion, axisAngleDeg),
    };
    if (hasCustomDir) {
      // Write the geometrically-reflected vector. When it stays custom it governs
      // the effective angle on re-read; when it collapsed to {1,0,0} the rotation
      // above carries the angle and this default vector is the correct storage.
      next.xAxisDirection = reflectedDir;
    }
    return next as unknown as T;
  }

  // Not a text-bearing entity — caller should not have routed it here; be
  // defensive with a plain coordinate mirror.
  return transformEntityCoords(entity, (p) => mirrorPoint(p, line1, line2));
}

/**
 * Rotate a 2D point around `center` by `angleDeg` degrees.
 * CCW positive — AutoCAD convention.
 */
export function rotatePoint(
  p: Point2D,
  center: Point2D,
  angleDeg: number,
): Point2D {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

/**
 * Scale a 2D point relative to `base` by `factor`.
 */
export function scalePoint(p: Point2D, base: Point2D, factor: number): Point2D {
  return {
    x: base.x + (p.x - base.x) * factor,
    y: base.y + (p.y - base.y) * factor,
  };
}

/**
 * Parallel offset of a line segment. The `side` point determines which
 * side the offset goes to (the offset direction is chosen so that the
 * result is closer to `side`).
 */
export function offsetLineSegment(
  start: Point2D,
  end: Point2D,
  distance: number,
  side: Point2D,
): { start: Point2D; end: Point2D } {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-14) {
    return { start: { ...start }, end: { ...end } };
  }
  // Normal vector (perpendicular to line direction)
  let nx = -dy / len;
  let ny = dx / len;

  // Choose the direction that is closer to `side`
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;
  const dotSide = (side.x - midX) * nx + (side.y - midY) * ny;
  if (dotSide < 0) {
    nx = -nx;
    ny = -ny;
  }

  return {
    start: { x: start.x + nx * distance, y: start.y + ny * distance },
    end: { x: end.x + nx * distance, y: end.y + ny * distance },
  };
}

/**
 * Expand or shrink a circle based on which side the user picked.
 * If `side` is outside the circle, the radius increases; if inside,
 * the radius decreases (but never below a minimum threshold).
 */
export function offsetCircle(
  center: Point2D,
  radius: number,
  distance: number,
  side: Point2D,
): { center: Point2D; radius: number } {
  const distToSide = Math.hypot(side.x - center.x, side.y - center.y);
  const newRadius =
    distToSide >= radius
      ? radius + distance
      : Math.max(1e-6, radius - distance);
  return { center: { x: center.x, y: center.y }, radius: newRadius };
}

// ── Entity-level angle/radius transforms ───────────────────────────

/**
 * For entities that have rotation or angle fields, add `angleDeg` to
 * those fields. Covers: Text rotation, MText rotation, Block rotation,
 * Arc startAngle/endAngle, Hatch arc/ellipticArc angles.
 *
 * ## What deliberately gets NO case here
 *
 * A field that is a DIRECTION rather than a rotation SCALAR already rides the
 * LINEAR part of the edit affine inside {@link transformEntityCoords}, and
 * ROTATE composes the two (`rotateEntityAngles(transformEntityCoords(…),
 * angleDeg)` — `geometryReducerEdit.ts`), so adding an arm here would turn it a
 * SECOND time. That is why there is no `Table` case (its orientation IS the
 * group-11 vector — see {@link spreadTableHorizontalDirection}) and no
 * `Polyline` case (a VERTEX's group-50 curve-fit tangent — see
 * {@link mapDirectionAngleThroughLinearPart}). Both are pinned by an explicit
 * double-rotation guard test in `geometryTransforms.polylineTableOle.test.ts`.
 */
export function rotateEntityAngles<T extends DrawingEntity>(
  entity: T,
  angleDeg: number,
): T {
  switch (entity.type) {
    case "Text":
      return { ...entity, rotation: (entity.rotation ?? 0) + angleDeg };
    case "MText":
      return { ...entity, rotation: (entity.rotation ?? 0) + angleDeg };
    case "Block":
      return {
        ...entity,
        rotation: (entity.rotation ?? 0) + angleDeg,
        // A WORLD-placed attribute's (`overridePosition` set) absolute angle
        // advances WITH the INSERT; a block-local one (unset — including a
        // value-only-edit `overrideRotation` with no position override) rides
        // `entity.rotation` above instead and is left untouched here — see
        // `withWorldAttributeRotation`'s doc for the full regime rationale
        // (local Codex review P1: gate on `overridePosition`, not
        // `overrideRotation`, or a block-local attribute double-rotates).
        // `?? []` — GitHub Codex review, PR #558 round 6 follow-up: a
        // regression-harness entity built from a minimal literal (not
        // schema-parsed, so `blockInsertEntitySchema.attributes`'
        // `.default([])` never fills in) can genuinely omit `attributes`;
        // `rotation` above already guards the same way (`?? 0`).
        attributes: (entity.attributes ?? []).map((attr) =>
          attr.overridePosition !== undefined
            ? withWorldAttributeRotation(
                attr,
                (attr.overrideRotation ?? 0) + angleDeg,
              )
            : attr,
        ),
      };
    case "Arc":
      return {
        ...entity,
        startAngle: entity.startAngle + angleDeg,
        endAngle: entity.endAngle + angleDeg,
      };
    case "Hatch":
      return {
        ...entity,
        boundaryPaths: entity.boundaryPaths.map((path) => ({
          ...path,
          edges: path.edges.map((edge) => {
            if (edge.edgeType === "arc") {
              return {
                ...edge,
                startAngle: edge.startAngle + angleDeg,
                endAngle: edge.endAngle + angleDeg,
              };
            }
            if (edge.edgeType === "ellipticArc") {
              return {
                ...edge,
                startAngle: edge.startAngle + angleDeg,
                endAngle: edge.endAngle + angleDeg,
              };
            }
            return edge;
          }),
        })),
      };
    default:
      return entity;
  }
}

/**
 * Scale radius, height, width, scaleX/Y, arrowheadSize etc. by
 * `factor`. The `base` parameter is used for entity-specific
 * calculations where needed. Only scalar fields are affected — point
 * coordinates are handled by `transformEntityCoords(scalePoint)`.
 */
// `scaleEntityRadii` now lives in its own module (2000-line file-size
// ceiling — see that file's header for the extraction rationale). Re-export
// so every existing `from "./geometryTransforms"` import path is unchanged.
export { scaleEntityRadii } from "./scaleEntityRadii";
// Re-exported here (the mechanical-move pattern `scaleEntityRadii` above
// follows) so the OLE refusal reaches `apps/web` through the barrel's existing
// `export * from "./geometryTransforms"` without a new index entry.
export {
  isAxisAlignedFramePreservingMap,
  isOleFrameTransformRefused,
  summarizeOleFrameTransformRefusal,
} from "./oleFrameTransformRefusal";

// ── Internal helpers ────────────────────────────────────────────────

function transformPoint(p: Point2D, fn: TransformFn): Point2D {
  return fn(p);
}

function transformOptionalPoint(
  p: Point2D | undefined,
  fn: TransformFn,
): Point2D | undefined {
  return p ? fn(p) : undefined;
}

function transformPoint3D(p: Point3D, fn: TransformFn): Point3D {
  const result = fn({ x: p.x, y: p.y });
  return { x: result.x, y: result.y, z: p.z };
}

function transformOptionalPoint3D(
  p: Point3D | undefined,
  fn: TransformFn,
): Point3D | undefined {
  if (!p) return undefined;
  const result = fn({ x: p.x, y: p.y });
  return { x: result.x, y: result.y, z: p.z };
}

/**
 * Transform a 3D *direction vector* (not a position) by the LINEAR part of the
 * affine transform `fn`. For an affine `fn(p) = A·p + b` the linear part is
 * `A·v = fn(v) − fn(origin)`. This is the correct treatment for a free vector:
 *
 *   - translate → unchanged (`b` cancels), so a MOVE leaves the vector alone;
 *   - rotate    → the direction rotates about the origin;
 *   - scale     → the magnitude scales with the geometry;
 *   - mirror    → the direction reflects.
 *
 * Applying the point transform directly (as for a position) would wrongly add
 * the translation `b` to a direction. Used for the MLEADER node `doglegVector`.
 * The z component is carried through unchanged (the 2D transforms never touch z).
 */
function transformOptionalVector3D(
  v: Point3D | undefined,
  fn: TransformFn,
): Point3D | undefined {
  if (!v) return undefined;
  const origin = fn({ x: 0, y: 0 });
  const moved = fn({ x: v.x, y: v.y });
  return { x: moved.x - origin.x, y: moved.y - origin.y, z: v.z };
}

/**
 * The 2x2 LINEAR part `L` of the affine `fn(p) = L·p + b`, recovered with three
 * PURE probes (`fn(0,0)`, `fn(1,0)`, `fn(0,1)`) — the translation `b` cancels
 * out. Same technique {@link transformOptionalVector3D} uses for a single free
 * vector, hoisted to ONE truth because three consumers now need `L` ITSELF
 * rather than one mapped vector: the handedness test
 * ({@link isOrientationReversing}), the ACAD_TABLE horizontal-direction map
 * ({@link spreadTableHorizontalDirection}) and the curve-fit tangent map
 * ({@link mapDirectionAngleThroughLinearPart}). The OCS-normal map moved to
 * `drawing-model`'s {@link transformOcsNormal}, which probes the 3D affine.
 *
 * Every `fn` this module is called with is affine — `translatePoint`,
 * `rotatePoint`, `scalePoint`, `mirrorPoint` and their compositions (see the
 * call sites in `geometryReducerEdit.ts` / `arrayReducers.ts`) — so three
 * probes determine it exactly.
 */
function linearPart(fn: TransformFn): {
  a: number;
  b: number;
  c: number;
  d: number;
} {
  const { a, b, c, d } = probeAffine2d(fn);
  return { a, b, c, d };
}

/** The implicit OCS extrusion normal of an entity that OMITS 210/220/230. */
const DEFAULT_EXTRUSION_NORMAL: Point3D = { x: 0, y: 0, z: 1 };

/** ≈ (0,0,1) within float noise — a normal that is the WCS default. */
function isDefaultExtrusionNormal(n: Point3D): boolean {
  return (
    Math.abs(n.x) < 1e-9 && Math.abs(n.y) < 1e-9 && Math.abs(n.z - 1) < 1e-9
  );
}

/**
 * SL-15 F2 + F8 + F-A — project a SPLINE's OCS extrusion NORMAL through the 2D
 * edit `fn`, returning a SPREADABLE `{ extrusion }` (or `{}` to keep it absent).
 *
 * ONE symmetric, by-construction rule (round-5 F-A, closing two P2s): compute the
 * transformed normal from the EFFECTIVE source (a present value, or the implicit
 * (0,0,1) default when absent), then MATERIALIZE it ONLY when BOTH
 *   (a) the spline is PLANAR, AND
 *   (b) the transformed normal is NOT the (0,0,1) WCS default;
 * otherwise OMIT it (canonical absent form).
 *
 * Why each guard:
 *  - (a) PLANAR — a NONPLANAR spline has NO OCS plane (Autodesk DXF R2018: the
 *    SPLINE normal 210/220/230 is "omitted if nonplanar"; ezdxf `spline.py`
 *    `extrusion = DXFAttr(210, default=Z_AXIS, optional=True)`). A
 *    handedness-changing MIRROR must NOT fabricate a (0,0,−1) normal for it — the
 *    absent normal STAYS absent (round-5 finding 1: pre-fix it materialized one
 *    regardless of `planar`).
 *  - (b) NON-DEFAULT — omitting a transformed normal that equals (0,0,1) makes the
 *    rule its own INVERSE (round-5 finding 4): planar absent → MIRROR → (0,0,−1)
 *    (present, ≠default) ✓; planar (0,0,−1) → UNDO-mirror → (0,0,1) == default →
 *    OMIT → absent ✓ (absence restored, not a lingering present (0,0,1)).
 *
 * Follows F2 for a present non-default normal on a planar spline (ROTATE rotates
 * the XY, MIRROR flips the det, SCALE re-tilts per the cofactor map, MOVE leaves
 * it) and F8 for materializing the reflected default under a planar MIRROR — both
 * are the `!isDefault(transformed) && planar` case. Orientation-PRESERVING edits
 * (MOVE / ROTATE-about-Z / uniform+positive SCALE) leave (0,0,1) → (0,0,1) →
 * OMIT, so a planar absent-normal spline never fabricates a group 210. (SL-15
 * F2: before this, the arm transformed only fit/control points and exported the
 * moved points under the STALE normal — the wrong plane on re-read.)
 */
function spreadSplineExtrusion(
  extrusion: Point3D | undefined,
  planar: boolean,
  fn: TransformFn,
): { extrusion: Point3D | undefined } {
  // ezdxf `transform_extrusion`: the derivation, the per-edit table (MOVE leaves
  // `n`; ROTATE turns its XY; MIRROR flips handedness — the `reflectExtrusion`
  // rule; uniform SCALE leaves `(0,0,1)` alone) and the degenerate-image
  // fallback all live on `drawing-model`'s {@link transformOcsNormal}, so this
  // arm and the `Polyline` arm's OCS-planar VERTEX re-expression
  // ({@link ocsPlanarTransform}) derive the new plane from ONE implementation.
  // `z` passes through because these edits embed in 3D as `M = blockdiag(L, 1)`;
  // the shared code also covers the block path's `blockdiag(L, sz)`.
  const source = extrusion ?? DEFAULT_EXTRUSION_NORMAL;
  const transformed =
    transformOcsNormal(source, (p) => ({ ...fn(p), z: p.z })) ?? source;
  // The `extrusion` key is ALWAYS returned (value or `undefined`) so the spread
  // in the Spline case OVERRIDES the `...entity` copy — omitting a normal must be
  // able to CLEAR a PRESENT source value, not just leave an absent one absent.
  //  - Nonplanar → no OCS plane → never materialize a normal (finding 1).
  //  - A transformed normal equal to the (0,0,1) default is the canonical ABSENT
  //    form — clear it so the mirror↔undo inverse is symmetric (finding 4).
  if (!planar || isDefaultExtrusionNormal(transformed)) {
    return { extrusion: undefined };
  }
  return { extrusion: transformed };
}

/**
 * The implicit ACAD_TABLE horizontal direction (DXF group 11/21/31) of a table
 * that OMITS the group — the Autodesk DXF reference's stated `(1, 0, 0)`
 * default, the same fallback {@link
 * import("@dxf-ai-editor/drawing-model").tableRotationDegrees} applies when the
 * vector is missing.
 *
 * targeted-cad-mutation-testing NOTE: Stryker reports the `ObjectLiteral`
 * mutant of this initialiser (`= {}`) as SURVIVED, and the round-13 run says
 * why — it is flagged `static: true` with `coveredBy: []`, the module-load-time
 * mutant class `stryker.cad.config.mjs`'s `coverageAnalysis` note describes,
 * evaluated before the runner can activate it. NOT a coverage hole: applied by
 * hand on this HEAD it fails "ROTATE materialises the direction of a table that
 * OMITS group 11", because an empty default makes that path produce `NaN`. An
 * earlier revision of this note claimed FOUR failing cases; the measured count
 * is ONE — the other three absent-group-11 tests expect an ABSENT field, which
 * `NaN` also yields through the `!(outLen > 0)` guard.
 */
// Stryker disable next-line ObjectLiteral: static (module-load-time) mutant the vitest runner cannot activate (`static: true`, `coveredBy: []` in the round-13 report) — the line IS covered, verified by hand as the note above records; same tooling limitation `packages/cad-core/src/common.ts` documents for its own module-scope const.
const DEFAULT_TABLE_HORIZONTAL_DIRECTION: Point3D = { x: 1, y: 0, z: 0 };

/**
 * Tolerance on the UNIT direction below which a mapped DIRECTION vector counts
 * as UNTURNED (see {@link spreadTableHorizontalDirection}). A chord of `1e-9`
 * between two unit vectors is ~1e-9 rad ≈ 5.7e-8° — far below any angle a
 * drawing can express, and the same order as the OCS-normal default test
 * {@link isDefaultExtrusionNormal} uses for the sibling "did this actually
 * change?" question.
 */
const DIRECTION_NOOP_TOLERANCE = 1e-9;

/**
 * Project an ACAD_TABLE's horizontal-direction vector (group 11/21/31) through
 * the 2D edit `fn`, returning a SPREADABLE `{ horizontalDirection? }` — the key
 * is present ONLY when the edit actually turned the table, so spreading `{}`
 * leaves whatever `...entity` already carried (a present group 11 keeps its
 * exact bytes; an absent one stays absent, with no own `undefined` key).
 *
 * ## Why the table needs this at all
 *
 * AcDbTable has NO rotation scalar: its orientation IS this vector, and every
 * consumer derives the angle from it with `tableRotationDegrees` — the render
 * adapter (`scene3d/adapters/typed/tableFromEntity.ts`), the bounds/index view
 * (`drawing-model/tableBlockReference.ts`, which feeds `rotation` from it) and
 * the DXF writer (`drawing-model/dxfBridge.entityConversion.ts`, group
 * 11/21/31) alike. So a ROTATE / MIRROR / negative SCALE that moved ONLY
 * `insertionPoint` orbited the `*T<N>` graphics around the base point while
 * leaving the table's own angle stale (GitHub Codex review PR #785 round 1,
 * F1). `rotateEntityAngles` deliberately gets NO `Table` case: rotation reaches
 * the table THROUGH this vector, and a second angle bump there would
 * DOUBLE-rotate it.
 *
 * ## The map
 *
 * A free direction transforms by the LINEAR part of the affine ({@link
 * linearPart} — translation cancelled, exactly like {@link
 * transformOptionalVector3D}). The image is then re-scaled to the SOURCE
 * vector's own XY magnitude, because AutoCAD stores a UNIT direction: without
 * that a `geometry/scale-entities` by 2 would rewrite `(1,0,0)` as `(2,0,0)` —
 * the SAME 0° angle, but a gratuitously rewritten group 11 (and, for a table
 * that had none, a FABRICATED one).
 *
 * ## The no-op snap, and what it does for PRESENCE
 *
 * `linearPart`'s three probes are float subtractions, so a MOVE by an awkward
 * offset recovers `L = I` only to within an ULP or two (`(1 + 0.001) − 0.001 =
 * 1.0000000000000002`) — enough to rewrite the stored vector's last bits on an
 * edit that geometrically did NOTHING to it. So the comparison is made between
 * the UNIT source and the UNIT image, and a difference within
 * `DIRECTION_NOOP_TOLERANCE` means "unturned": the field is left exactly as it
 * was. That single rule gives all three behaviours the DXF round-trip needs:
 *   - MOVE / COPY and any positive uniform SCALE are bit-exact no-ops — a
 *     present group 11 keeps its bytes, an ABSENT one stays absent;
 *   - ROTATE / MIRROR / negative SCALE write the turned vector, materialising
 *     it for a table that omitted the group (whose implied direction is the
 *     DXF reference's `(1,0,0)`) — otherwise the default 0° would silently win
 *     at render, bounds and export;
 *   - once materialised the field stays explicit, exactly as
 *     `presencePreservingValue`'s repo-wide rule prescribes for an optional
 *     field a transform has rewritten.
 *
 * targeted-cad-mutation-testing EQUIVALENT MUTANT: EqualityOperator `<=` →
 * `<` on the snap comparison below. Why equivalent: the two differ ONLY when
 * the unit-chord is bit-exactly `1e-9` — a decimal literal with no exact
 * binary representation, which `Math.hypot` would have to land on to the ULP
 * from a probed affine's rounding. No affine an edit command can produce is
 * constructible to hit it, and AT that tie the two outcomes are
 * indistinguishable anyway: either the group is left alone or it is rewritten
 * by a 1e-9 rad (5.7e-8°) turn, a difference below every angle this model can
 * represent. Nothing narrower than the whole comparison can express the
 * boundary, so there is no test to write for it.
 *
 * A degenerate IMAGE (zero XY length — a collapsing `fn`, or a stored vector
 * like `(0,0,1)` whose XY part is already zero, for which
 * `tableRotationDegrees` reports 0° regardless) is left exactly as it was
 * rather than having a direction invented for it. There is deliberately no
 * SEPARATE zero-SOURCE guard: `L·0 = 0` for every linear map, so a zero-XY
 * source always produces a zero-XY image and is caught by this one test.
 */
function spreadTableHorizontalDirection(
  dir: Point3D | undefined,
  fn: TransformFn,
): { horizontalDirection?: Point3D } {
  const src = dir ?? DEFAULT_TABLE_HORIZONTAL_DIRECTION;
  const { a, b, c, d } = linearPart(fn);
  const srcLen = Math.hypot(src.x, src.y);
  const outX = a * src.x + c * src.y;
  const outY = b * src.x + d * src.y;
  const outLen = Math.hypot(outX, outY);
  if (!(outLen > 0)) return {};
  const unitOutX = outX / outLen;
  const unitOutY = outY / outLen;
  const unitSrcX = src.x / srcLen;
  const unitSrcY = src.y / srcLen;
  if (
    Math.hypot(unitOutX - unitSrcX, unitOutY - unitSrcY) <=
    DIRECTION_NOOP_TOLERANCE
  ) {
    return {};
  }
  return {
    horizontalDirection: {
      x: unitOutX * srcLen,
      y: unitOutY * srcLen,
      z: src.z,
    },
  };
}

/**
 * Map a stored DIRECTION ANGLE (degrees, CCW from +X in the entity's own plane)
 * through the LINEAR part `L` of the edit affine, returning the turned angle —
 * or `undefined` when the field must be LEFT EXACTLY AS AUTHORED.
 *
 * ## Why an angle rides the linear part and not `rotateEntityAngles`
 *
 * A direction is a FREE VECTOR: it must reflect under MIRROR and shear under a
 * non-uniform SCALE, neither of which a scalar `+ angleDeg` can express. This
 * is the ANGLE-valued twin of {@link spreadTableHorizontalDirection}'s
 * vector-valued map — same {@link linearPart}, same
 * {@link DIRECTION_NOOP_TOLERANCE} unit-chord no-op test — and the same rule
 * the typed block-flatten path applies to the SAME heavy-POLYLINE field
 * (`scene3d/adapters/typed/transformBlockChild.ts` → `mapDirectionAngleDeg`,
 * whose non-uniform branch is exactly `atan2` of the direction carried through
 * the linear map). The render path decomposes an INSERT affine into
 * rotation/scale/mirror and can therefore use an exact closed-form table for
 * the similarity case; a `TransformFn` arrives here as an opaque affine, so the
 * one derivation that covers every case is the vector map.
 *
 * ## The no-op snap, and what it protects
 *
 * `linearPart`'s three probes are float subtractions, so a MOVE by an awkward
 * offset recovers `L = I` only to within an ULP or two — enough for `atan2` to
 * rewrite a stored `123.456` as `123.45600000000002` on an edit that
 * geometrically did NOTHING to the direction. The comparison is therefore made
 * between the UNIT source and the UNIT image, and a difference within
 * `DIRECTION_NOOP_TOLERANCE` (~5.7e-8°, below any angle a drawing expresses)
 * means "unturned": `undefined` is returned and the caller leaves the stored
 * bytes alone, so MOVE / COPY / positive uniform SCALE are bit-exact no-ops on
 * the field.
 *
 * A DEGENERATE image (zero length — a collapsing `fn`, or a non-finite stored
 * angle) likewise returns `undefined` rather than inventing a direction, the
 * same guard `spreadTableHorizontalDirection` applies to its own image.
 *
 * The result is normalised to `[0, 360)`, the range the DXF reference gives
 * group 50 (`atan2` alone returns `(-180, 180]`, so a turned 350° tangent would
 * otherwise be stored as a negative angle the file format does not use).
 *
 * targeted-cad-mutation-testing EQUIVALENT MUTANT: EqualityOperator `<=` → `<`
 * on the snap comparison below. Identical to the one
 * {@link spreadTableHorizontalDirection} documents for the SAME comparison
 * against the SAME constant, for the same reason: the two differ ONLY when the
 * unit-chord is bit-exactly `1e-9`, a decimal literal with no exact binary
 * representation that a probed affine's rounding would have to land on to the
 * ULP — and AT that tie the outcomes are indistinguishable anyway (leave the
 * field alone, or rewrite it by a 5.7e-8° turn). Nothing narrower than the whole
 * comparison can express the boundary, so there is no test to write for it.
 */
function mapDirectionAngleThroughLinearPart(
  angleDeg: number,
  linear: { a: number; b: number; c: number; d: number },
): number | undefined {
  const rad = (angleDeg * Math.PI) / 180;
  // Already a UNIT vector, so the source needs no normalisation of its own.
  const srcX = Math.cos(rad);
  const srcY = Math.sin(rad);
  const outX = linear.a * srcX + linear.c * srcY;
  const outY = linear.b * srcX + linear.d * srcY;
  const outLen = Math.hypot(outX, outY);
  if (!(outLen > 0)) return undefined;
  if (
    Math.hypot(outX / outLen - srcX, outY / outLen - srcY) <=
    DIRECTION_NOOP_TOLERANCE
  ) {
    return undefined;
  }
  const turned = (Math.atan2(outY, outX) * 180) / Math.PI;
  let wrapped = turned % 360;
  // targeted-cad-mutation-testing EQUIVALENT MUTANT: EqualityOperator `<` →
  // `<=` here. At `wrapped === 0` the mutant adds 360 and the clamp below
  // immediately returns it to 0, so both forms emit the same value; the only
  // input that could tell them apart is a negative zero from `Math.atan2`,
  // which needs an image of exactly `(−0, +x)` — unreachable from a unit
  // vector carried through a probed affine, and indistinguishable once
  // written (`formatDxfNumber` emits `0.0` for both).
  if (wrapped < 0) wrapped += 360;
  // A sub-ULP NEGATIVE image (rotating a 0° tangent by +90° and back lands on
  // `-1e-15`) adds 360 and ROUNDS to exactly 360 — outside the half-open range
  // group 50 uses, and a gratuitously different byte for the same direction.
  // Pinned by "a tangent whose image lands a sub-ULP below zero is stored as 0,
  // never 360" (the shrunk counterexample of the `[0, 360)` property).
  if (wrapped >= 360) wrapped = 0;
  return wrapped;
}

/** Transform an MLEADER break point (start/end positions, optional z) by `fn`,
 * preserving any z and the non-coordinate `breakIndex`. Shared by the leader-line
 * and node-level break arrays so both walk identically (Codex #266). */
function transformMLeaderBreak<
  T extends {
    start: { x: number; y: number; z?: number };
    end: { x: number; y: number; z?: number };
  },
>(bp: T, fn: TransformFn): T {
  return {
    ...bp,
    start: transformBreakEndpoint(bp.start, fn),
    end: transformBreakEndpoint(bp.end, fn),
  };
}

function transformBreakEndpoint<P extends { x: number; y: number; z?: number }>(
  p: P,
  fn: TransformFn,
): P {
  const moved = fn({ x: p.x, y: p.y });
  return p.z !== undefined
    ? ({ ...p, x: moved.x, y: moved.y, z: p.z } as P)
    : ({ ...p, x: moved.x, y: moved.y } as P);
}

/**
 * Transform all coordinate fields within Hatch boundary paths. Each
 * edge type (line, arc, ellipticArc) has its own set of point fields.
 */
function transformHatchBoundaryPaths(
  paths: HatchBoundaryPath[],
  fn: TransformFn,
): HatchBoundaryPath[] {
  return paths.map((path) => ({
    ...path,
    edges: path.edges.map((edge) => {
      switch (edge.edgeType) {
        case "line":
          return {
            ...edge,
            start: transformPoint(edge.start, fn),
            end: transformPoint(edge.end, fn),
          };
        case "arc":
          return {
            ...edge,
            center: transformPoint(edge.center, fn),
          };
        case "ellipticArc":
          return {
            ...edge,
            center: transformPoint(edge.center, fn),
            majorAxisEnd: transformPoint(edge.majorAxisEnd, fn),
          };
        default:
          return edge;
      }
    }),
  }));
}

// ── Main entity transformer ─────────────────────────────────────────

/**
 * Deep-clone `entity` with every coordinate field transformed by `fn`.
 *
 * The implementation handles all entity types in the DrawingEntity
 * discriminated union by switching on `entity.type`. Each branch
 * knows which fields are coordinate-bearing and applies `fn` to them.
 *
 * Adding a new entity type? Add a `case` here — TypeScript's exhaustive
 * switch (the `default` at the bottom) will remind you.
 */
export function transformEntityCoords<T extends DrawingEntity>(
  entity: T,
  fn: TransformFn,
): T {
  switch (entity.type) {
    case "Line":
      return {
        ...entity,
        start: transformPoint(entity.start, fn),
        end: transformPoint(entity.end, fn),
      };

    case "Point":
      return {
        ...entity,
        location: transformPoint(entity.location, fn),
      };

    case "Circle":
      return {
        ...entity,
        center: transformPoint(entity.center, fn),
      };

    case "Arc":
      return {
        ...entity,
        center: transformPoint(entity.center, fn),
      };

    case "Ellipse":
      // SL-15 F1 + round-9 @codex F2 — an ELLIPSE has a full 3D FRAME (center incl.
      // z, the 3D major-axis DIRECTION, the minor/major ratio, the OCS extrusion
      // normal). A direct MOVE/COPY/ROTATE/SCALE/MIRROR must transform the WHOLE
      // frame — pre-fix this arm moved ONLY the center, so a ROTATE/MIRROR/SCALE of
      // an elevated / tilted ellipse left its orientation + tilt STALE. The full
      // 3D-frame math (identity-gated translation fast-path + the one-truth
      // `computeExplodedEllipseGeometry` for the rotate/scale/mirror linear part)
      // lives in {@link ellipseEditFrame}; spread its override fields over the
      // entity exactly like the Spline arm spreads `spreadSplineExtrusion`.
      return {
        ...entity,
        ...ellipseEditFrame(entity, fn),
      };

    case "Spline":
      return {
        ...entity,
        fitPoints: entity.fitPoints.map((point) => transformPoint3D(point, fn)),
        controlPoints: entity.controlPoints.map((point) =>
          transformPoint3D(point, fn),
        ),
        // SL-15 F2 + F8 + F-A: transform the OCS extrusion NORMAL consistently
        // with the points via the ONE symmetric materialization rule — a normal is
        // produced ONLY for a PLANAR spline whose transformed normal is non-default
        // (a nonplanar spline never fabricates one; a transformed-to-default normal
        // is omitted so mirror↔undo restores absence). `planar` (DXF group 70 bit
        // 8) is threaded through so the nonplanar guard holds (see
        // {@link spreadSplineExtrusion}).
        ...spreadSplineExtrusion(entity.extrusion, entity.planar, fn),
      };

    case "LWPOLYLINE":
      return {
        ...entity,
        vertices: entity.vertices.map((v) => {
          const transformed = fn({ x: v.x, y: v.y });
          return { ...v, x: transformed.x, y: transformed.y };
        }),
      };

    case "Text":
      return {
        ...entity,
        insertionPoint: transformPoint(entity.insertionPoint, fn),
        alignmentPoint: transformOptionalPoint(entity.alignmentPoint, fn),
      };

    case "MText":
      return {
        ...entity,
        insertionPoint: transformPoint(entity.insertionPoint, fn),
        // Codex #342: the X-axis DIRECTION vector (group 11/21/31) is a free
        // direction that must move WITH the entity under the LINEAR part of the
        // transform — a MOVE leaves it unchanged, but ROTATE / SCALE must
        // reflect/rotate it. (MIRROR of TEXT/MTEXT no longer routes HERE — both
        // MIRRTEXT=0 and MIRRTEXT=1 go through mirrorTextReadable / mirrorTextFull
        // respectively, which handle rotation + direction together. This case is
        // still reached by a MIRROR of a NON-text entity, but those have no
        // xAxisDirection, so the branch below is inert for them.) Reuses the same
        // `transformOptionalVector3D` helper as the Multileader `doglegVector`
        // (no math clone); Z preserved.
        //
        // F3 (round-2): transform the vector ONLY when it is GENUINELY custom.
        // `xAxisDirection` defaults to `{1,0,0}` (schema default, NOT undefined),
        // so a rotation-driven MTEXT (angle in group 50) arrives with `{1,0,0}`.
        // Transforming that unconditionally MANUFACTURES a non-default group 11
        // from the transform alone (e.g. ROTATE a 45° rotation-driven MTEXT by
        // 30° → rotation stays 75° but the vector becomes a 30° direction; group
        // 11 takes precedence on export → reloads as 30°). For a default
        // direction we leave it default so `rotation` governs; a custom direction
        // still transforms. Rotation itself is handled by rotateEntityAngles /
        // scaleEntityRadii where applicable — transformEntityCoords never rewrites
        // it, which is correct (the default-dir angle lives in `rotation`).
        ...(isCustomXAxisDirection(entity.xAxisDirection)
          ? {
              xAxisDirection: transformOptionalVector3D(
                entity.xAxisDirection,
                fn,
              ),
            }
          : {}),
      };

    case "Dimension":
      return {
        ...entity,
        defPoint: transformPoint(entity.defPoint, fn),
        textMidpoint: transformPoint(entity.textMidpoint, fn),
        extLine1Point: transformOptionalPoint(entity.extLine1Point, fn),
        extLine2Point: transformOptionalPoint(entity.extLine2Point, fn),
        thirdPoint: transformOptionalPoint3D(entity.thirdPoint, fn),
        arcPoint: transformOptionalPoint3D(entity.arcPoint, fn),
      };

    case "Multileader":
      return {
        ...entity,
        textLocation: transformPoint(entity.textLocation, fn),
        basePoint: transformOptionalPoint(entity.basePoint, fn),
        leaderRoots: entity.leaderRoots.map((root) => ({
          ...root,
          nodes: root.nodes.map((node) => ({
            ...node,
            // Node-level CONTEXT_DATA coords must move WITH the entity (Codex
            // #266). Before this the transform only walked the leader-line
            // vertices/breaks, leaving the typed LeaderData coords stale after
            // MOVE/ROTATE/SCALE: `lastLeaderPoint` (10/20/30 — a position),
            // the node `breakPoints` (12↔13 — positions), and `doglegVector`
            // (11/21/31 — a free direction, so the LINEAR part only: a MOVE
            // leaves it unchanged, ROTATE/SCALE/MIRROR apply).
            lastLeaderPoint: transformOptionalPoint3D(node.lastLeaderPoint, fn),
            doglegVector: transformOptionalVector3D(node.doglegVector, fn),
            ...(node.breakPoints
              ? {
                  breakPoints: node.breakPoints.map((bp) =>
                    transformMLeaderBreak(bp, fn),
                  ),
                }
              : {}),
            lines: node.lines.map((line) => ({
              ...line,
              vertices: line.vertices.map((v) => transformPoint(v, fn)),
              // Guard a possibly-undefined breakPoints (Zod-bypass / programmatic
              // state): the leader-line transform must not throw on move/grip of a
              // leader-line without breaks — mirrors the dxfBridge export guard
              // (entityConversion.ts) for the same crash class.
              breakPoints: (line.breakPoints ?? []).map((bp) =>
                transformMLeaderBreak(bp, fn),
              ),
            })),
          })),
        })),
      };

    case "Hatch":
      return {
        ...entity,
        boundaryPaths: transformHatchBoundaryPaths(entity.boundaryPaths, fn),
        seedPoints: entity.seedPoints.map((sp) => transformPoint(sp, fn)),
      };

    case "Block":
      return {
        ...entity,
        insertionPoint: transformPoint(entity.insertionPoint, fn),
        // Codex MIRRTEXT-DIMENSION-MLEADER-ATTRIB: a block-attribute's WORLD
        // `overridePosition` (EATTEDIT absolute position override — set) is
        // an absolute point independent of the INSERT's own affine
        // (`blockInsertAttributeSchema.overridePosition`'s documented WORLD
        // invariant; the render — `attdefFromEntity.ts resolvePlacement` —
        // places it there WITHOUT re-applying the parent INSERT transform).
        // Every modify command that reaches this shared function (MOVE /
        // COPY / ROTATE / SCALE / MIRROR / OFFSET) must move it WITH the
        // block, or the attribute text visually detaches from its INSERT —
        // confirmed absent before this fix (`overridePosition` was never
        // touched here at all). A block-local attribute
        // (`overridePosition === undefined`) is left untouched — it rides
        // the INSERT's OWN (already-transformed) affine at render time, a
        // separate, pre-existing path this fix does not change.
        //
        // NOTE — this does NOT make MIRROR of the Block/INSERT ITSELF fully
        // AutoCAD-faithful: `entity.rotation` / `scaleX` / `scaleY` are still
        // untouched by mirror here (a real block-reference mirror is
        // represented by a negative scale factor in AutoCAD/DXF, which this
        // reducer does not yet author — a separate, pre-existing gap, tracked
        // as a follow-up, NOT introduced or masked by this fix). A
        // block-local (non-overridden) attribute therefore still does not
        // visually mirror with its INSERT; a WORLD-overridden attribute's
        // OWN point/rotation now mirrors correctly regardless.
        // `?? []` — GitHub Codex review, PR #558 round 6 follow-up: a
        // regression-harness entity built from a minimal literal (not
        // schema-parsed, so `blockInsertEntitySchema.attributes`'
        // `.default([])` never fills in) can genuinely omit `attributes`.
        attributes: (entity.attributes ?? []).map((attr) =>
          attr.overridePosition !== undefined
            ? {
                ...attr,
                overridePosition: transformPoint(attr.overridePosition, fn),
              }
            : attr,
        ),
      };

    case "Rectangular Associative Array":
      return {
        ...entity,
        basePoint: transformPoint(entity.basePoint, fn),
      };

    case "Revision Cloud":
      return {
        ...entity,
        vertices: entity.vertices.map((v) => {
          const transformed = fn({ x: v.x, y: v.y });
          return { ...v, x: transformed.x, y: transformed.y };
        }),
      };

    case "Wipeout":
      return {
        ...entity,
        vertices: entity.vertices.map((v) => transformPoint(v, fn)),
      };

    case "Viewport":
      // A generic geometry transform (MOVE / COPY / ROTATE — and the linear
      // SCALE path below) moves/copies the VIEWPORT entity but NOT the separate
      // paperspace SPATIAL_FILTER boundary its clip handle (340) points at. So a
      // COPYed clipped viewport would duplicate the handle (two viewports → one
      // boundary) and a MOVEd/ROTATEd one would point at an un-transformed
      // boundary (wrong clip). Invalidate the clip (handle + group-90 0x10000
      // non-rect bit + legacy extraGroups-340) so the transformed viewport never
      // carries a WRONG clip. Re-authoring a transformed boundary is slice-3
      // (S-VIEWPORT-CLIP-RESOLVE). The cast is safe: this branch's `entity` is a
      // ViewportEntity, but the generic `T` parameter erases that to the union.
      return invalidateViewportClip({
        ...(entity as ViewportEntity),
        center: transformPoint(entity.center, fn),
      }) as T;

    case "Polyline": {
      // Legacy heavy POLYLINE. Every VERTEX record's location moves with the
      // entity; Z is preserved verbatim because `fn` is a 2D planar map (the
      // same contract every other kind here relies on — see the LWPOLYLINE /
      // Revision Cloud arms, which likewise keep the vertex's non-planar
      // fields). Flags, the per-vertex handle and the face records are
      // transform-invariant. Widths are SIZE-like, so they belong to the SCALE
      // companion (`scaleEntityRadii`'s own `Polyline` case), not here.
      //
      // GitHub Codex review PR #785 round 1, F2 — BULGE HANDEDNESS. A vertex
      // bulge is `tan(θ/4)` of the arc that replaces the straight segment to
      // the NEXT vertex, and its SIGN says which side of the chord the arc
      // bows to (this repo's one-truth decoder, `drawing-model/bulgeArc.ts`:
      // "positive bulge = CCW from v1 to v2 in the global frame, the arc
      // bowing to the LEFT of the chord direction"). A reflection maps "left
      // of the chord" onto "right of the REFLECTED chord", so a mirror that
      // moved only the endpoints put every arc on the WRONG side — the
      // renderer (`scene3d/adapters/typed/polylineFromEntity.ts`) and the DXF
      // writer both read the sign verbatim, so the wrong arc was drawn AND
      // exported. On the DEFAULT-extrusion path below, negate the bulge exactly
      // when the transform REVERSES orientation (`det(L) < 0` — MIRROR only; a
      // negative uniform SCALE is a 180° rotation with `det = f² > 0` and must
      // NOT flip). Same rule the typed block-flatten path applies to a mirrored
      // child (`transformBlockChild.ts`'s `mapBulge`).
      //
      // Gated on the group-70 SUBTYPE: group 42 is a 2D-polyline field per the
      // Autodesk DXF reference, and the renderer reads bulges ONLY for `"2d"`
      // (`polylineFromEntity.ts`: "A 3D polyline has no bulges … BY
      // CONSTRUCTION"), so a stray 42 on a 3D/mesh VERTEX is passed through
      // untouched rather than silently rewritten. That same gate keeps a 3D
      // polyline / mesh — whose vertices are already WCS — off the OCS path.
      //
      // Presence: an ABSENT bulge (⇒ 0) stays absent — never materialise a
      // group 42 the source never had — and an explicit `0` stays `+0` (the
      // `v.bulge` truthiness guard), so no `-0` reaches the writer.
      //
      // @codex round 4, P2 — OCS. A NON-DEFAULT group-210 extrusion makes these
      // vertices OCS, so `fn` applied to them edits the wrong numbers (under
      // `(0,0,-1)` a WCS `+10 X` MOVE renders as `-10 X`). `ocsPlanarTransform`
      // is ezdxf's `OCSTransform`; it returns `null` for the default extrusion,
      // so the common planar case stays BIT-EXACT. Its header carries the
      // derivation, the Autodesk-over-ezdxf choice there, why the general path
      // must NOT also flip bulges (its in-plane map always has det > 0 — hence
      // the `ocs === null` conjunct on `flipBulges`), and the KNOWN RESIDUAL —
      // an ANISOTROPIC stretch of a TILTED plane (SCALE embeds `diag(f, f, 1)`,
      // a similarity of R³ only for `|f| = 1`) degrades the BOW *and*, for a
      // large `|f|`, the POSITIONS. Mechanism, measured bound and completion
      // plan: that header (not restated — this file is at its 2500-line cap).
      const is2d = polylineSubtype(entity) === "2d";
      const lift = (p: Point3D): Point3D => ({ ...fn(p), z: p.z });
      const ocs = is2d
        ? ocsPlanarTransform(entity.extrusion, entity.elevation.z, lift)
        : null;
      const flipBulges = is2d && ocs === null && isOrientationReversingMap(fn);
      // GitHub Codex review PR #785 round 3, P2 — CURVE-FIT TANGENT DIRECTIONS.
      // A VERTEX with group-70 bit 2 (`DXF_VERTEX_FLAG.CURVE_FIT_TANGENT`)
      // carries a group-50 tangent DIRECTION in degrees, and AutoCAD refits the
      // curve through it when the drawing is reopened. Moving the vertex while
      // leaving the tangent at its PRE-transform bearing therefore saved a
      // curve that re-fits differently from the one on screen — the direction
      // sibling of the bulge-handedness defect above, and the same field the
      // typed block-flatten path already maps
      // (`scene3d/adapters/typed/transformBlockChild.ts`). It rides the LINEAR
      // part of the affine through {@link mapDirectionAngleThroughLinearPart}
      // (reflects under MIRROR, shears under a non-uniform SCALE, is a
      // bit-exact no-op under MOVE / COPY / positive uniform SCALE) — and on the
      // OCS path through the IN-PLANE map, from the same basis pair the vertices
      // go through, because mapping a direction through the raw WCS affine while
      // the plane moved underneath it is a desync.
      //
      // `rotateEntityAngles` deliberately gets NO `Polyline` case — the exact
      // trap `spreadTableHorizontalDirection`'s doc records for `Table`: ROTATE
      // runs `rotateEntityAngles(transformEntityCoords(…), angleDeg)`
      // (`geometryReducerEdit.ts`), so an arm there would add the rotation a
      // SECOND time on top of the linear map and double-turn every tangent.
      // Handling it here instead also covers MIRROR and ARRAY-with-rotation,
      // neither of which is guaranteed to reach `rotateEntityAngles` at all.
      //
      // The linear part is probed ONCE per entity, and only when some vertex
      // actually carries a tangent (three `fn` calls that a plain polyline —
      // the overwhelming majority — never pays).
      //
      // Presence: an ABSENT group 50 stays absent (`!== undefined` gate) — a
      // tangent is never fabricated for a vertex that had none — and an
      // UNTURNED one keeps its exact stored value (`undefined` from the map).
      //
      // targeted-cad-mutation-testing EQUIVALENT MUTANTS, all four on the two
      // guards below, all from the SAME invariant — `tangentLinearPart` is
      // defined exactly when some vertex carries a group 50, and
      // {@link mapDirectionAngleThroughLinearPart} returns `undefined` for a
      // non-finite angle (`Math.cos(NaN)` → a NaN image → the `!(outLen > 0)`
      // guard). So:
      //   · `ConditionalExpression → true` on the `.some(...)` probe guard —
      //     computes `linearPart` unconditionally. PURE PERFORMANCE: the value
      //     is only read behind `v.curveFitTangent !== undefined`, which is
      //     false for every vertex of a polyline that has no tangents.
      //   · `ConditionalExpression → true` on either operand of the per-vertex
      //     `&&`, and `&&` → `||`: the only reachable extra call is
      //     `map(undefined, L)`, which returns `undefined` by the NaN guard →
      //     no group 50 written, exactly as before. (The reverse pairing,
      //     `map(angle, undefined)`, is unreachable: the first operand is false
      //     whenever `tangentLinearPart` is undefined.) The "a vertex with NO
      //     group 50 never gets one fabricated" property test pins the
      //     behaviour these mutants preserve.
      // The DISCRIMINATING mutant on the same line — `!==` → `===` in the
      // `.some(...)` — is killed by "maps EVERY tangent when no vertex is
      // without one" (a fixture in which the negated predicate is false).
      // Re-verified on the OCS form below (@codex round 4): `ocs?.linear` is an
      // object whenever `ocs` is non-null, so `tangentLinearPart` is still
      // undefined EXACTLY when the `.some(...)` is false — invariant unchanged.
      const tangentLinearPart = entity.vertices.some(
        (v) => v.curveFitTangent !== undefined,
      )
        ? (ocs?.linear ?? linearPart(fn))
        : undefined;
      return {
        ...entity,
        // On the OCS path the plane MOVED. `extrusion: undefined` CLEARS a group
        // 210 whose image is the WCS default — the inverse-symmetry rule
        // {@link spreadSplineExtrusion} documents for the SPLINE normal.
        ...(ocs ? { extrusion: ocs.extrusion } : {}),
        ...(ocs ? { elevation: { ...entity.elevation, z: ocs.elevation } } : {}),
        vertices: entity.vertices.map((v) => {
          const moved = ocs
            ? ocs.vertex(v.location.x, v.location.y)
            : { ...fn({ x: v.location.x, y: v.location.y }), z: v.location.z };
          const turnedTangent =
            tangentLinearPart !== undefined && v.curveFitTangent !== undefined
              ? mapDirectionAngleThroughLinearPart(
                  v.curveFitTangent,
                  tangentLinearPart,
                )
              : undefined;
          return {
            ...v,
            location: { ...v.location, x: moved.x, y: moved.y, z: moved.z },
            ...(flipBulges && v.bulge ? { bulge: -v.bulge } : {}),
            ...(turnedTangent !== undefined
              ? { curveFitTangent: turnedTangent }
              : {}),
          };
        }),
      };
    }

    case "Table":
      // A table is placed by its insertion point, exactly like the block
      // reference it is in DXF — same treatment as `Block` above. The `*T<N>`
      // graphics block's own content is NOT touched: it is shared definition
      // geometry, and moving a table must not move every other reference to
      // that definition.
      //
      // GitHub Codex review PR #785 round 1, F1 — the table's ORIENTATION is
      // the horizontal-direction vector (group 11/21/31), not a rotation
      // scalar, so it has to turn WITH the insertion point or the `*T<N>`
      // content orbits the base point at its old angle. See
      // {@link spreadTableHorizontalDirection} for the linear-part map, the
      // magnitude/presence rules, and why `rotateEntityAngles` must NOT also
      // grow a `Table` case.
      //
      // KNOWN INCOMPLETE — a table does NOT RESIZE here (Codex PR #785 round
      // 2, P2). This arm moves/turns the table but cannot change its SIZE:
      // `rowHeights` (141) / `columnWidths` (142) are read-only projections of
      // `contentGroups`, and the visible geometry is the pre-evaluated `*T<N>`
      // graphics block (`tableAsBlockReference`, hard-coded unit scale), which
      // has no generator — so writing 141/142 alone would desync the data from
      // the picture. `geometry/scale-entities` therefore DECLINES a table at
      // `|factor| !== 1` instead of half-transforming it, visibly, via
      // {@link import("./tableScaleRefusal").isTableScaleRefused} — read that
      // module's header for the desync proof and what completing it requires.
      // Every OTHER edit (MOVE / ROTATE / MIRROR / ARRAY, and a `|factor| === 1`
      // point reflection) reaches a table through this arm exactly and is
      // unaffected.
      // REFUSAL (@codex round 15, FINDING 2). A REFLECTION is the one map the
      // insertion point + horizontal-direction pair cannot name: mirroring
      // `(1, 0)` about Y gives `(-1, 0)`, which `tableAsBlockReference` (unit
      // scale, no handedness field) re-places as a 180° TURN — left and UP
      // where the reflection is left and DOWN. The block-child EXPLODE path
      // already declines exactly this case (`explodeChildRefusal`'s `sx*sy > 0`
      // orientation conjunct); the predicate both now read is the leaf module
      // {@link import("./tableMirrorRefusal").isOrientationReversingMap}. The
      // SAME `entity` is returned, so the refusal is a true no-op.
      if (isOrientationReversingMap(fn)) return entity;
      // `transformPoint3D`, NOT `transformPoint`: group 30 is an elevation the
      // 2D helper drops (@codex round 13 — proof in the ELEVATION block of
      // `geometryTransforms.polylineTableOle.test.ts`).
      return {
        ...entity,
        insertionPoint: transformPoint3D(entity.insertionPoint, fn),
        ...spreadTableHorizontalDirection(entity.horizontalDirection, fn),
      };

    case "OLE Frame": {
      // An OLE2FRAME is stored as TWO opposite WCS corners (10/20/30 and
      // 11/21/31) and carries NO rotation field, so the only placements it can
      // express are the ones that leave the frame axis-aligned.
      //
      // REFUSAL (@codex PR #785 round 13, FINDING C). An OBLIQUE map has no
      // such placement, and answering it with the axis-aligned BOX of the image
      // persists a DIFFERENT rectangle — a 20x10 frame turned 45 degrees came
      // back 21.213x21.213, straight into the 10/20 + 11/21 groups
      // `dxf-io/writer.ts` writes. The predicate that decides this, with the
      // Autodesk / ezdxf sources and the per-edit table, is the leaf module
      // {@link import("./oleFrameTransformRefusal").isAxisAlignedFramePreservingMap};
      // the SAME `entity` object is returned when it says no, so a refusal is a
      // true no-op that no downstream identity check can mistake for an edit.
      //
      // Every map that DOES reach the fold below (MOVE, a multiple-of-90-degree
      // ROTATE, an axis or diagonal MIRROR, any SCALE) sends the four corners
      // onto the four corners of a new axis-aligned rectangle, so the fold is
      // the EXACT affine image and nothing is approximated. It folds all FOUR
      // corners, not the two stored ones (round 3, P2): the stored pair is a
      // DIAGONAL, and its image misses the extremes the other two reach.
      //
      // The corner ROLES are read off the SOURCE's own ordering rather than
      // re-canonicalised, so a MOVE writes back exactly the corners it read (a
      // byte-identical round trip) while a quarter turn re-labels them
      // consistently. z is carried per corner (`transformPoint3D`'s rule — `fn`
      // is a 2D planar map), which the pre-round-3 two-corner spread DROPPED.
      if (!isAxisAlignedFramePreservingMap(fn)) {
        return entity;
      }
      const { upperLeft: ul, lowerRight: lr } = entity;
      const corners = [
        fn({ x: ul.x, y: ul.y }),
        fn({ x: lr.x, y: ul.y }),
        fn({ x: lr.x, y: lr.y }),
        fn({ x: ul.x, y: lr.y }),
      ];
      // targeted-cad-mutation-testing EQUIVALENT MUTANTS: EqualityOperator
      // `<` → `<=` / `>` → `>=` on the four comparisons of this min/max fold.
      // At equality the assignment writes a value NUMERICALLY equal to the one
      // the accumulator holds, so both forms compute the identical box — the
      // standard min/max-idiom equivalence. The one non-BIT-identical case is
      // SIGNED ZERO (`-0 < +0` false, `-0 <= +0` true), so a mirror that maps a
      // corner onto `-0` lets the mutant store `-0` for `+0` (verified, not
      // assumed). Unobservable: the two are `===` everywhere downstream, and
      // `formatDxfNumber(-0)` emits `"0"` (integer branch → `String(-0)`; the
      // non-integer branch has its own `"-0" → "0"` guard).
      // (The corner-ROLE comparisons below are NOT of this kind and are killed
      // by the two degenerate-frame cases.)
      let minX = corners[0]!.x;
      let maxX = corners[0]!.x;
      let minY = corners[0]!.y;
      let maxY = corners[0]!.y;
      for (const corner of corners) {
        if (corner.x < minX) minX = corner.x;
        if (corner.x > maxX) maxX = corner.x;
        if (corner.y < minY) minY = corner.y;
        if (corner.y > maxY) maxY = corner.y;
      }
      // The source's own corner roles: `upperLeft` normally holds the smaller x
      // and the larger y, but a file may store the pair either way round and a
      // no-op edit must not rewrite that choice.
      const upperLeftHoldsMinX = ul.x <= lr.x;
      const upperLeftHoldsMaxY = ul.y >= lr.y;
      return {
        ...entity,
        upperLeft: {
          x: upperLeftHoldsMinX ? minX : maxX,
          y: upperLeftHoldsMaxY ? maxY : minY,
          z: ul.z,
        },
        lowerRight: {
          x: upperLeftHoldsMinX ? maxX : minX,
          y: upperLeftHoldsMaxY ? minY : maxY,
          z: lr.z,
        },
      };
    }

    default: {
      // Exhaustive check — if a new entity type is added to
      // DrawingEntity, TypeScript will error here until a case is added.
      const _exhaustiveCheck: never = entity;
      return _exhaustiveCheck;
    }
  }
}

// ── Batch 5: Intersection & modification helpers ───────────────────

/**
 * Compute the intersection of two infinite lines defined by segments.
 * Returns null if the lines are parallel or nearly so.
 */
export function lineLineIntersection(
  seg1Start: Point2D,
  seg1End: Point2D,
  seg2Start: Point2D,
  seg2End: Point2D,
): Point2D | null {
  const d1x = seg1End.x - seg1Start.x;
  const d1y = seg1End.y - seg1Start.y;
  const d2x = seg2End.x - seg2Start.x;
  const d2y = seg2End.y - seg2Start.y;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-10) return null;
  const t =
    ((seg2Start.x - seg1Start.x) * d2y -
      (seg2Start.y - seg1Start.y) * d2x) /
    denom;
  return {
    x: seg1Start.x + t * d1x,
    y: seg1Start.y + t * d1y,
  };
}

/**
 * Find intersection points of a line (infinite extension of segment)
 * with a circle. Returns 0, 1, or 2 intersection points.
 */
export function lineCircleIntersection(
  lineStart: Point2D,
  lineEnd: Point2D,
  center: Point2D,
  radius: number,
): Point2D[] {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const fx = lineStart.x - center.x;
  const fy = lineStart.y - center.y;

  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - radius * radius;

  let discriminant = b * b - 4 * a * c;
  if (discriminant < -1e-10) return [];
  if (discriminant < 0) discriminant = 0;

  const results: Point2D[] = [];
  const sqrtD = Math.sqrt(discriminant);

  const t1 = (-b - sqrtD) / (2 * a);
  results.push({ x: lineStart.x + t1 * dx, y: lineStart.y + t1 * dy });

  if (discriminant > 1e-10) {
    const t2 = (-b + sqrtD) / (2 * a);
    results.push({ x: lineStart.x + t2 * dx, y: lineStart.y + t2 * dy });
  }

  return results;
}

/**
 * Find intersection points of a line with an arc (partial circle).
 * Filters lineCircleIntersection results to only points within the
 * arc's angular range [startAngle, endAngle] in degrees.
 */
export function lineArcIntersection(
  lineStart: Point2D,
  lineEnd: Point2D,
  center: Point2D,
  radius: number,
  startAngle: number,
  endAngle: number,
): Point2D[] {
  const circleHits = lineCircleIntersection(lineStart, lineEnd, center, radius);
  return circleHits.filter((pt) => {
    const angle =
      ((Math.atan2(pt.y - center.y, pt.x - center.x) * 180) / Math.PI + 360) %
      360;
    const sa = ((startAngle % 360) + 360) % 360;
    const ea = ((endAngle % 360) + 360) % 360;
    if (sa <= ea) {
      return angle >= sa - 0.01 && angle <= ea + 0.01;
    }
    // Arc wraps around 0 degrees
    return angle >= sa - 0.01 || angle <= ea + 0.01;
  });
}

/**
 * Trim a line segment at a trimPoint. The `keepSide` point indicates
 * which side of the trim point to keep. Returns the trimmed segment.
 */
export function trimLineAtPoint(
  start: Point2D,
  end: Point2D,
  trimPoint: Point2D,
  keepSide: Point2D,
): { start: Point2D; end: Point2D } {
  const dStartToKeep = Math.hypot(keepSide.x - start.x, keepSide.y - start.y);
  const dEndToKeep = Math.hypot(keepSide.x - end.x, keepSide.y - end.y);

  if (dStartToKeep < dEndToKeep) {
    // Keep the start side
    return { start: { ...start }, end: { x: trimPoint.x, y: trimPoint.y } };
  }
  // Keep the end side
  return { start: { x: trimPoint.x, y: trimPoint.y }, end: { ...end } };
}

/**
 * Extend a line to reach a target point. The `nearEnd` point indicates
 * which endpoint of the line to extend. Projects the targetPoint onto
 * the line's infinite extension.
 */
export function extendLineToPoint(
  start: Point2D,
  end: Point2D,
  targetPoint: Point2D,
  nearEnd: Point2D,
): { start: Point2D; end: Point2D } {
  const dStart = Math.hypot(nearEnd.x - start.x, nearEnd.y - start.y);
  const dEnd = Math.hypot(nearEnd.x - end.x, nearEnd.y - end.y);

  if (dEnd <= dStart) {
    // Extend the end point
    return { start: { ...start }, end: { x: targetPoint.x, y: targetPoint.y } };
  }
  // Extend the start point
  return { start: { x: targetPoint.x, y: targetPoint.y }, end: { ...end } };
}

/**
 * Compute a fillet between two line segments. If radius is 0, just find
 * the intersection (sharp corner). Returns trimmed lines and an optional
 * arc entity.
 *
 * AutoCAD parity: when `pickPoint1` / `pickPoint2` are supplied, the
 * surviving piece of each line is the half on the same side of the
 * intersection as the pick — matching AutoCAD's "the click chooses the
 * side that survives" convention. Without pick points, the algorithm
 * defaults to keeping `l1Start` and `l2End` (legacy behaviour, used by
 * call sites that don't expose a pick context).
 *
 * Concrete failure mode the pick-point logic fixes (Phase I, 2026-04-25):
 * lines that meet AT the intersection (e.g. l1 = (-180,0)→(0,0) and
 * l2 = (0,0)→(0,-180)) have only ONE side per line on which the user
 * can pick. The legacy algorithm enumerated four (sign1, sign2) combos
 * and returned the first that satisfied the perpendicular-distance test
 * — which routinely landed in the wrong quadrant, producing an arc
 * outside the corner pocket and EXTENDING the picked lines instead of
 * trimming them. With pick-side filtering, the only valid combo is the
 * one whose tangent points lie between the picked endpoint and the
 * intersection (i.e. on the surviving arm).
 */
export function filletTwoLines(
  l1Start: Point2D,
  l1End: Point2D,
  l2Start: Point2D,
  l2End: Point2D,
  radius: number,
  pickPoint1?: Point2D,
  pickPoint2?: Point2D,
): {
  line1: { start: Point2D; end: Point2D };
  line2: { start: Point2D; end: Point2D };
  arc: {
    center: Point2D;
    radius: number;
    startAngle: number;
    endAngle: number;
  } | null;
} {
  const ix = lineLineIntersection(l1Start, l1End, l2Start, l2End);
  if (!ix) {
    // Parallel lines — return unchanged
    return {
      line1: { start: { ...l1Start }, end: { ...l1End } },
      line2: { start: { ...l2Start }, end: { ...l2End } },
      arc: null,
    };
  }

  // ── Determine which endpoint of each line to keep ───────────────
  // AutoCAD parity: the side of the line that contains the pick is
  // the side that survives. The right discriminator is "which side
  // of the intersection is the pickPoint on, projected onto the
  // line's direction" — NOT raw distance to each endpoint, because a
  // pick at the segment midpoint is equidistant from both endpoints
  // yet still belongs unambiguously to one side of the intersection
  // (specifically the side that doesn't contain the intersection).
  //
  // For line1 with direction (l1End - l1Start), parameter `t1ix` of
  // the intersection along the line is in [0..1] (intersection lies
  // within the segment). The endpoint OPPOSITE the pick across `t1ix`
  // is what survives — i.e. if pickPoint1 projects to `t1pick > t1ix`,
  // the +direction side survives → keep `l1End`. Else keep `l1Start`.
  //
  // Without pick points, fall back to the legacy convention
  // (keep l1Start, keep l2End) for backward compatibility.
  function pickedEndIsLineEnd(
    pick: Point2D | undefined,
    start: Point2D,
    end: Point2D,
    intersect: Point2D,
    fallback: boolean,
  ): boolean {
    if (pick === undefined) return fallback;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-14) return fallback;
    const tIx = ((intersect.x - start.x) * dx + (intersect.y - start.y) * dy) /
      lenSq;
    const tPick = ((pick.x - start.x) * dx + (pick.y - start.y) * dy) / lenSq;
    // If both pick and the line end are on the +direction side of the
    // intersection (tPick > tIx and 1 > tIx), keep end. Otherwise keep
    // start. Equality (tPick == tIx) falls through to the fallback —
    // that case means the user clicked exactly on the intersection,
    // which AutoCAD treats as ambiguous.
    if (Math.abs(tPick - tIx) < 1e-10) return fallback;
    return tPick > tIx;
  }

  const keep1End = pickedEndIsLineEnd(pickPoint1, l1Start, l1End, ix, false);
  const keep2End = pickedEndIsLineEnd(pickPoint2, l2Start, l2End, ix, true);

  const kept1: Point2D = keep1End ? l1End : l1Start;
  const kept2: Point2D = keep2End ? l2End : l2Start;

  if (radius <= 1e-10) {
    // Sharp corner (radius=0): trim both lines to intersection,
    // keeping the picked side.
    return {
      line1: keep1End
        ? { start: { x: ix.x, y: ix.y }, end: { ...l1End } }
        : { start: { ...l1Start }, end: { x: ix.x, y: ix.y } },
      line2: keep2End
        ? { start: { x: ix.x, y: ix.y }, end: { ...l2End } }
        : { start: { ...l2Start }, end: { x: ix.x, y: ix.y } },
      arc: null,
    };
  }

  // Compute fillet arc center: offset both lines inward by radius,
  // find the intersection of offset lines.
  const d1x = l1End.x - l1Start.x;
  const d1y = l1End.y - l1Start.y;
  const len1 = Math.hypot(d1x, d1y);
  const d2x = l2End.x - l2Start.x;
  const d2y = l2End.y - l2Start.y;
  const len2 = Math.hypot(d2x, d2y);

  if (len1 < 1e-14 || len2 < 1e-14) {
    return {
      line1: { start: { ...l1Start }, end: { ...l1End } },
      line2: { start: { ...l2Start }, end: { ...l2End } },
      arc: null,
    };
  }

  // Normal vectors (pointing toward the fillet side)
  const n1x = -d1y / len1;
  const n1y = d1x / len1;
  const n2x = -d2y / len2;
  const n2y = d2x / len2;

  // Try all four (sign1, sign2) combos and prefer the one whose tangent
  // points lie BETWEEN the kept endpoint and the intersection — i.e.
  // both t1 and t2 fall on the surviving arm. This is the geometrically
  // unique fillet matching the user's pick.
  type Candidate = {
    sign1: 1 | -1;
    sign2: 1 | -1;
    center: Point2D;
    t1: Point2D;
    t2: Point2D;
    score: number;
  };
  const candidates: Candidate[] = [];

  for (const sign1 of [1, -1] as const) {
    for (const sign2 of [1, -1] as const) {
      const o1s: Point2D = {
        x: l1Start.x + sign1 * n1x * radius,
        y: l1Start.y + sign1 * n1y * radius,
      };
      const o1e: Point2D = {
        x: l1End.x + sign1 * n1x * radius,
        y: l1End.y + sign1 * n1y * radius,
      };
      const o2s: Point2D = {
        x: l2Start.x + sign2 * n2x * radius,
        y: l2Start.y + sign2 * n2y * radius,
      };
      const o2e: Point2D = {
        x: l2End.x + sign2 * n2x * radius,
        y: l2End.y + sign2 * n2y * radius,
      };
      const center = lineLineIntersection(o1s, o1e, o2s, o2e);
      if (!center) continue;

      // Project center onto both original lines to get tangent points
      const t1 = projectPointOnLine(center, l1Start, l1End);
      const t2 = projectPointOnLine(center, l2Start, l2End);
      if (!t1 || !t2) continue;

      // Verify distance is approximately radius
      const dist1 = Math.hypot(center.x - t1.x, center.y - t1.y);
      const dist2 = Math.hypot(center.x - t2.x, center.y - t2.y);
      if (Math.abs(dist1 - radius) > 0.1 || Math.abs(dist2 - radius) > 0.1)
        continue;

      // Score this candidate by how well it matches the picked sides:
      // higher score for tangent points lying BETWEEN the kept endpoint
      // and the intersection.
      const t1OnPickedSide = pointBetween(t1, kept1, ix, 0.1);
      const t2OnPickedSide = pointBetween(t2, kept2, ix, 0.1);
      let score = 0;
      if (t1OnPickedSide) score += 2;
      if (t2OnPickedSide) score += 2;
      // Tie-breaker: prefer t1/t2 inside [start, end] of the original
      // segment (true "trim" semantics over "extend" semantics).
      if (pointOnSegment(t1, l1Start, l1End, 0.1)) score += 1;
      if (pointOnSegment(t2, l2Start, l2End, 0.1)) score += 1;

      candidates.push({ sign1, sign2, center, t1, t2, score });
    }
  }

  if (candidates.length === 0) {
    // Fallback: sharp corner using picked sides
    return {
      line1: keep1End
        ? { start: { x: ix.x, y: ix.y }, end: { ...l1End } }
        : { start: { ...l1Start }, end: { x: ix.x, y: ix.y } },
      line2: keep2End
        ? { start: { x: ix.x, y: ix.y }, end: { ...l2End } }
        : { start: { ...l2Start }, end: { x: ix.x, y: ix.y } },
      arc: null,
    };
  }

  // Pick the highest-scoring candidate
  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0]!;
  const { center, t1, t2 } = best;

  // Build the trimmed lines so the picked endpoint survives.
  const line1 = keep1End
    ? { start: t1, end: { ...l1End } }
    : { start: { ...l1Start }, end: t1 };
  const line2 = keep2End
    ? { start: t2, end: { ...l2End } }
    : { start: { ...l2Start }, end: t2 };

  // Compute arc start/end angles. The renderer (`describeArc` in
  // `apps/web/src/app/scene/renderHelpers.ts`) draws the arc CCW from
  // `startAngle` to `endAngle` and computes the SVG sweep span as
  // `((endAngle - startAngle) mod 360 + 360) mod 360`. So if we pick
  // (sa, ea) in the wrong order, the renderer draws the *long* arc
  // around the back instead of the short fillet arc inside the corner
  // pocket. Fillet arcs are always ≤ 180° (they round a corner formed
  // by two rays meeting at the intersection), so the right order is
  // the one whose CCW span is ≤ 180°.
  const a1 = (Math.atan2(t1.y - center.y, t1.x - center.x) * 180) / Math.PI;
  const a2 = (Math.atan2(t2.y - center.y, t2.x - center.x) * 180) / Math.PI;
  const span12 = (((a2 - a1) % 360) + 360) % 360;
  const sa = span12 <= 180 ? a1 : a2;
  const ea = span12 <= 180 ? a2 : a1;

  return {
    line1,
    line2,
    arc: { center, radius, startAngle: sa, endAngle: ea },
  };
}

/**
 * Returns true when point `p` lies between the two endpoints `a` and
 * `b` along the line through them, with at most `tol` perpendicular
 * deviation. Endpoints (parameter t == 0 or t == 1) count as "between".
 */
function pointBetween(
  p: Point2D,
  a: Point2D,
  b: Point2D,
  tol: number,
): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-14) return false;
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  if (t < -tol / Math.sqrt(lenSq)) return false;
  if (t > 1 + tol / Math.sqrt(lenSq)) return false;
  // Perpendicular distance check
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  const perp = Math.hypot(p.x - projX, p.y - projY);
  return perp <= tol;
}

/**
 * Returns true when point `p` lies on segment [a, b] within `tol`
 * perpendicular tolerance. Equivalent to `pointBetween` but with the
 * naming aligned to "segment containment".
 */
function pointOnSegment(
  p: Point2D,
  a: Point2D,
  b: Point2D,
  tol: number,
): boolean {
  return pointBetween(p, a, b, tol);
}

/** Project point onto infinite line and return foot of perpendicular. */
function projectPointOnLine(
  p: Point2D,
  lineStart: Point2D,
  lineEnd: Point2D,
): Point2D | null {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-14) return null;
  const t = ((p.x - lineStart.x) * dx + (p.y - lineStart.y) * dy) / lenSq;
  return { x: lineStart.x + t * dx, y: lineStart.y + t * dy };
}

/**
 * Compute a chamfer between two line segments. Trims both lines and
 * returns the chamfer line segment.
 */
export function chamferTwoLines(
  l1Start: Point2D,
  l1End: Point2D,
  l2Start: Point2D,
  l2End: Point2D,
  dist1: number,
  dist2: number,
): {
  line1: { start: Point2D; end: Point2D };
  line2: { start: Point2D; end: Point2D };
  chamferLine: { start: Point2D; end: Point2D };
} | null {
  const ix = lineLineIntersection(l1Start, l1End, l2Start, l2End);
  if (!ix) return null;

  // Direction vectors from intersection
  const d1x = l1Start.x - ix.x;
  const d1y = l1Start.y - ix.y;
  const len1 = Math.hypot(d1x, d1y);
  const d2x = l2End.x - ix.x;
  const d2y = l2End.y - ix.y;
  const len2 = Math.hypot(d2x, d2y);

  if (len1 < 1e-14 || len2 < 1e-14) return null;

  // Chamfer points on each line at the specified distances from intersection
  const p1: Point2D = {
    x: ix.x + (d1x / len1) * dist1,
    y: ix.y + (d1y / len1) * dist1,
  };
  const p2: Point2D = {
    x: ix.x + (d2x / len2) * dist2,
    y: ix.y + (d2y / len2) * dist2,
  };

  return {
    line1: { start: { ...l1Start }, end: p1 },
    line2: { start: p2, end: { ...l2End } },
    chamferLine: { start: p1, end: p2 },
  };
}

/**
 * Lengthen (or shorten) a line by a delta, percentage, or to a total
 * length. The `nearEnd` indicates which endpoint to move.
 *
 * Modes:
 * - "delta": add `value` to the line length (negative = shorten)
 * - "percent": multiply the line length by `value / 100`
 * - "total": set the line length to `value`
 */
export function lengthenLine(
  start: Point2D,
  end: Point2D,
  mode: "delta" | "percent" | "total",
  value: number,
  nearEnd: Point2D,
): { start: Point2D; end: Point2D } {
  const currentLen = Math.hypot(end.x - start.x, end.y - start.y);
  if (currentLen < 1e-14) return { start: { ...start }, end: { ...end } };

  let newLen: number;
  switch (mode) {
    case "delta":
      newLen = currentLen + value;
      break;
    case "percent":
      newLen = currentLen * (value / 100);
      break;
    case "total":
      newLen = value;
      break;
  }
  newLen = Math.max(1e-6, newLen);

  const dStart = Math.hypot(nearEnd.x - start.x, nearEnd.y - start.y);
  const dEnd = Math.hypot(nearEnd.x - end.x, nearEnd.y - end.y);

  if (dEnd <= dStart) {
    // Move the end point
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    return {
      start: { ...start },
      end: {
        x: start.x + (dx / currentLen) * newLen,
        y: start.y + (dy / currentLen) * newLen,
      },
    };
  }
  // Move the start point
  const dx = start.x - end.x;
  const dy = start.y - end.y;
  return {
    start: {
      x: end.x + (dx / currentLen) * newLen,
      y: end.y + (dy / currentLen) * newLen,
    },
    end: { ...end },
  };
}

/**
 * Decompose an LWPOLYLINE into individual Line entities. Each segment
 * of the polyline becomes a separate Line. Arc segments (bulge != 0)
 * are approximated as straight lines for now — full arc decomposition
 * requires converting bulge to an Arc entity.
 */
export function explodePolylineToLines(
  vertices: Array<{ x: number; y: number; bulge?: number }>,
  closed: boolean,
  spaceId: string,
  idPrefix: string,
): Array<{
  type: "Line";
  spaceId: string;
  id: string;
  start: Point2D;
  end: Point2D;
}> {
  const result: Array<{
    type: "Line";
    spaceId: string;
    id: string;
    start: Point2D;
    end: Point2D;
  }> = [];
  const segCount = closed ? vertices.length : vertices.length - 1;
  for (let i = 0; i < segCount; i++) {
    const j = (i + 1) % vertices.length;
    result.push({
      type: "Line",
      spaceId,
      id: `${idPrefix}-seg-${i + 1}`,
      start: { x: vertices[i].x, y: vertices[i].y },
      end: { x: vertices[j].x, y: vertices[j].y },
    });
  }
  return result;
}

/**
 * Check whether two points are within a tolerance distance of each other.
 */
export function pointsMatch(
  a: Point2D,
  b: Point2D,
  tolerance: number = 0.01,
): boolean {
  return Math.hypot(a.x - b.x, a.y - b.y) <= tolerance;
}

/**
 * Distance from a point to the nearest point on a line segment.
 */
export function distancePointToSegment(
  point: Point2D,
  segStart: Point2D,
  segEnd: Point2D,
): number {
  const dx = segEnd.x - segStart.x;
  const dy = segEnd.y - segStart.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-14) return Math.hypot(point.x - segStart.x, point.y - segStart.y);
  let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = segStart.x + t * dx;
  const projY = segStart.y + t * dy;
  return Math.hypot(point.x - projX, point.y - projY);
}
